<template>
  <div>
    <!-- Breadcrumbs for normal mode -->
    <div v-if="!embedded" class="p-6 pb-0">
      <BreadCrumbs :navigation="navigation" />
    </div>
    
    <!-- Back button for embedded mode -->
    <div v-else class="p-4 border-b flex items-center justify-between">
      <button 
        @click="goBackToHome" 
        class="flex items-center text-gray-600 hover:text-gray-800"
      >
        <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
        </svg>
        {{ $t('go.actions.back_to_orders') }}
      </button>
      <h3 class="text-lg font-medium text-gray-900">
        {{ $t('go.doc_status.' + localHead?.doc_status) }}
        {{ localHead?.doc_no || '' }}
      </h3>
    </div>


    <ConfirmationModal :show="showDeleteModal" @close="showDeleteModal = false">
        <template #title>
            {{ $t('go.modals.confirm_delete_title') }}
        </template>

        <template #content>
            {{ $t('go.modals.confirm_delete_content') }}
            <span v-if="localHead.doc_status === 'order' && localHead.mago_doc_order_id" class="text-red-900"><br></br>{{ $t('go.modals.confirm_delete_mago_note', { doc_no: localHead.mago_doc_order_no }) }}</span>
            <span v-if="localHead.doc_status === 'offert' && localHead.mago_doc_quota_id" class="text-red-900"><br></br>{{ $t('go.modals.confirm_delete_mago_quota_note', { doc_no: localHead.mago_doc_quota_no }) }}</span>
        </template>

        <template #footer>
            <SecondaryButton @click="showDeleteModal = false">
                {{ $t('go.actions.cancel') }}
            </SecondaryButton>

            <DangerButton
                class="ms-3"
                @click="deleteDocument"
            >
                {{ $t('go.actions.delete') }}
            </DangerButton>
        </template>
    </ConfirmationModal>

    <ConfirmationModal :show="showDeleteModalRow" @close="showDeleteModalRow = false">
        <!-- Conferma prima di eliminare -->
        <template #title>
            {{ $t('go.modals.confirm_delete_row_title') }}
        </template>

        <template #content>
            {{ $t('go.modals.confirm_delete_row_content') }}
        </template>

        <template #footer>
            <SecondaryButton @click="showDeleteModalRow = false">
                {{ $t('go.actions.cancel') }}
            </SecondaryButton>

            <DangerButton
                class="ms-3"
                @click="deleteDetail(toDelete)"
            >
                {{ $t('go.actions.delete') }}
            </DangerButton>
        </template>
    </ConfirmationModal>

    <ConfirmationModal :show="showSignatureConfirm" @close="showSignatureConfirm = false">
        <template #title>
            {{ $t('go.modals.confirm_signature_title') }}
        </template>

        <template #content>
            {{ $t('go.modals.confirm_signature_content') }}
        </template>

        <template #footer>
            <SecondaryButton @click="showSignatureConfirm = false">
                {{ $t('go.actions.cancel') }}
            </SecondaryButton>

            <DangerButton
                class="ms-3"
                @click="saveSignature()"
            >
                {{ $t('go.actions.confirm') }}
            </DangerButton>
        </template>
    </ConfirmationModal>

    <ConfirmationModal :show="showWarnQtyModal" @close="showWarnQtyModal = false">
        <template #title>
            {{ $t('go.modals.min_reorder_qty_title') }}
        </template>

        <template #content>
            {{ $t('go.modals.min_reorder_qty_content', { qty: warnQty }) }}
        </template>

        <template #footer>
            <SecondaryButton @click="showWarnQtyModal = false">
                {{ $t('go.actions.cancel') }}
            </SecondaryButton>

            <DangerButton
                class="ms-3"
                @click="confirmApplyDetailChanges"
            >
                {{ $t('go.actions.continue') }}
            </DangerButton>
        </template>
    </ConfirmationModal>

    <ConfirmationModal :show="showAgentConfirmModal" @close="showAgentConfirmModal = false">
        <template #title>
            Aggiorna Agente
        </template>

        <template #content>
            L'agente associato a questo cliente ({{ localHead.customer_data.SalesPerson }}) è diverso da quello attualmente impostato nel documento. Desideri aggiornarlo?
        </template>

        <template #footer>
            <SecondaryButton @click="showAgentConfirmModal = false">
                Annulla
            </SecondaryButton>

            <DangerButton
                class="ms-3"
                @click="confirmAgentChange"
            >
                Aggiorna
            </DangerButton>
        </template>
    </ConfirmationModal>
    
    <ConfirmationModal :show="showLockModal" :closeable="false" @close="showLockModal = false">
        <template #title>
            <span v-if="$page.props.isAdminOrSuperAdmin">{{ $t('go.modals.document_locked_force_title') }}</span>
            <span v-else>{{ $t('go.modals.document_locked_title') }}</span>
        </template>
        <template #content>
            <span v-if="$page.props.isAdminOrSuperAdmin">{{ $t('go.modals.document_locked_force_content', { user: documentLock?.user_name }) }}</span>
            <span v-else>{{ $t('go.modals.document_locked_content', { user: documentLock?.user_name }) }}</span>
        </template>
        <template #footer>
            <SecondaryButton @click="$emit('go-back-to-home')">
                {{ $t('go.actions.back_to_orders') }}
            </SecondaryButton>
            <SecondaryButton class="ms-3" @click="forcedReadOnly = true; showLockModal = false">
                {{ $t('go.modals.document_view_readonly') }}
            </SecondaryButton>
            <DangerButton v-if="$page.props.isAdminOrSuperAdmin" class="ms-3" @click="forzaAccesso">
                {{ $t('go.modals.document_force_access') }}
            </DangerButton>
        </template>
    </ConfirmationModal>

    <ConfirmationModal :show="showKickedModal" :closeable="false" @close="onKickedConfirm">
        <template #title>{{ $t('go.modals.document_kicked_title') }}</template>
        <template #content>{{ $t('go.modals.document_kicked_content') }}</template>
        <template #footer>
            <DangerButton @click="onKickedConfirm">
                {{ $t('go.actions.back_to_orders') }}
            </DangerButton>
        </template>
    </ConfirmationModal>

    <ConfirmationModal :show="showCreateContractModal" @close="showCreateContractModal = false">
        <template #title>Crea Contratto</template>

        <template #content>
            <p class="mb-3">Seleziona le righe dell'ordine da riportare nel nuovo contratto:</p>
            <div v-if="contractEligibleDetails.length === 0" class="text-gray-500">
                Nessuna riga disponibile per la creazione di un contratto.
            </div>
            <div v-else class="max-h-80 overflow-y-auto border rounded-md">
                <table class="min-w-full">
                    <thead>
                        <tr class="bg-gray-100">
                            <th class="p-2 text-left">
                                <input type="checkbox" v-model="selectAllContractRows" @change="toggleSelectAllContractRows">
                            </th>
                            <th class="p-2 text-left">Codice</th>
                            <th class="p-2 text-left">Descrizione</th>
                            <th class="p-2 text-right">Qtà</th>
                            <th class="p-2 text-right">Prezzo</th>
                            <th class="p-2 text-left">Contratto</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="detail in contractEligibleDetails" :key="detail.id" class="border-t" :class="{ 'bg-gray-50 text-gray-400': detail.contract_docno }">
                            <td class="p-2">
                                <input type="checkbox" :value="detail.id" v-model="selectedContractRowIds" :disabled="!!detail.contract_docno">
                            </td>
                            <td class="p-2">{{ detail.item }}</td>
                            <td class="p-2">{{ detail.description }}</td>
                            <td class="p-2 text-right">{{ formatNumber(detail.qty) }}</td>
                            <td class="p-2 text-right">{{ formatCurrency(detail.unit_value, 3) }}</td>
                            <td class="p-2 text-left">{{ detail.contract_docno || '-' }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </template>

        <template #footer>
            <SecondaryButton @click="showCreateContractModal = false">
                {{ $t('go.actions.cancel') }}
            </SecondaryButton>

            <button
                class="ms-3 bg-teal-500 disabled:bg-teal-300 text-white px-4 py-2 rounded hover:bg-teal-600"
                :disabled="selectedContractRowIds.length === 0"
                @click="createContractFromOrder"
            >
                Crea Contratto
            </button>
        </template>
    </ConfirmationModal>

    <br v-if="!embedded">
    <div class="p-6 space-y-6 bg-gray-50 shadow-md rounded-lg">

        <Loader :visible="isLoading" :text="loadingText" />
        <!-- Top bar -->
      <div class="flex flex-col lg:flex-row justify-between items-start p-0 gap-4">
        <div class="flex flex-wrap gap-2">
          <button 
            class="px-4 py-2 rounded relative"
            :class="hasUnsavedChanges ? 'bg-orange-500 disabled:bg-orange-300 text-white hover:bg-orange-600' : 'bg-blue-400 disabled:bg-blue-300 text-white hover:bg-blue-500'"
            @click="saveChanges" :disabled="blockedEdits">
            <span v-if="hasUnsavedChanges">{{ $t('go.actions.save_changes_unsaved') }}</span>
            <span v-else>{{ $t('go.actions.save_changes') }}</span>
          </button>
          <button class="bg-yellow-400 disabled:bg-yellow-300 text-white px-4 py-2 rounded hover:bg-yellow-500"
            v-if="localHead?.doc_status == 'draft'" @click="localHead.doc_status = 'offert'; hasUnsavedChanges = true" :disabled="blockedEdits">{{ $t('go.actions.convert_to_offer') }}</button>
          <button class="bg-orange-400 disabled:bg-orange-300 text-white px-4 py-2 rounded hover:bg-orange-500"
            v-if="localHead?.doc_status == 'order' && localHead?.id == -1 && $policy('importFromOffert').canView"
            @click="showTableOffert"
            v-bind="$policy('importFromOffert').props">{{ $t('go.actions.import_from_offer') }}
          </button>
          <!-- Invia a mago bloccato anche per l'admin prima di correzione -->
          <button class="bg-yellow-400 disabled:bg-yellow-300 text-white px-4 py-2 rounded hover:bg-yellow-500"
            v-if="localHead?.doc_status == 'order' && !hasUnsavedChanges && localHead?.id > 0" @click="sendToMago" :disabled="blockedEdits || (forceAdminCorrection && localHead?.approval == 1)">
              <span v-if="!localHead?.mago_doc_order_no">{{ $t('go.actions.send_to_mago') }}</span>
              <span v-else>{{ $t('go.actions.edit_on_mago') }}</span>
          </button>
          <button class="bg-yellow-400 disabled:bg-yellow-300 text-white px-4 py-2 rounded hover:bg-yellow-500"
            v-if="enableOfferMagoSend && localHead?.doc_status == 'offert' && !hasUnsavedChanges && localHead?.id > 0" @click="sendQuoteToMago" :disabled="blockedEdits">
              <span v-if="!localHead?.mago_doc_quota_no">{{ $t('go.actions.send_to_mago') }}</span>
              <span v-else>{{ $t('go.actions.edit_on_mago') }}</span>
          </button>
          <button class="bg-cyan-400 disabled:bg-cyan-300 text-white px-4 py-2 rounded hover:bg-cyan-500"
            v-if="handleSignature > 0 && localHead?.doc_status == 'order' && localHead?.id > 0 && !blockedEdits" @click="showSignatureModal = true">
              <span>{{ $t('go.actions.customer_signature') }}</span>
          </button>
          <button class="bg-teal-500 disabled:bg-teal-300 text-white px-4 py-2 rounded hover:bg-teal-600"
            v-if="enableContractCreation && localHead?.doc_status == 'order' && localHead?.mago_doc_order_no && $policy('createContract').canView" @click="openCreateContractModal" :disabled="$policy('createContract').props.disabled">
              <span>Crea Contratto</span>
          </button>
          <button class="bg-violet-400 disabled:bg-violet-300 text-white px-4 py-2 rounded hover:bg-violet-500"
            v-if="showDMSContent && localHead?.doc_status == 'order' && localHead?.id > 0 && localHead?.mago_doc_order_no" @click="loadDMSRows">
              <span>{{ $t('go.actions.dms') }}</span>
          </button>
          <button class="bg-red-500 disabled:bg-red-300 text-white px-4 py-2 rounded hover:bg-red-600"
            v-if="localHead?.id > 0 && $policy('deleteDoc').canView" @click="showDeleteModal = true" :disabled="blockedEdits || $policy('deleteDoc').props.disabled">
            <span>{{ $t('go.actions.delete') }}</span>
          </button>
          <button class="bg-indigo-500 disabled:bg-indigo-300 text-white px-4 py-2 rounded hover:bg-indigo-600"
            v-if="localHead?.id > 0 && $policy('duplicateDoc').canView" @click="duplicateDocument" :disabled="isLoading || $policy('duplicateDoc').props.disabled">
            <span>Duplica</span>
          </button>
          <button class="bg-green-500 disabled:bg-green-300 text-white px-4 py-2 rounded hover:bg-green-600"
            v-if="localHead?.id > 0 && (localHead?.doc_status != 'draft' || allowDraftPrint) && !hasUnsavedChanges && $policy('printDoc').canView" :disabled="(!localHead?.customer_data?.CustSupp && !localHead?.customer_data?.Contact) || $policy('printDoc').props.disabled" @click="printDocument">
            <v-icon name="co-print" class="mr-1" />
          </button>
          <button class="bg-slate-500 text-white px-4 py-2 rounded hover:bg-slate-600 disabled:bg-slate-300"
            v-if="emailSend &&localHead?.id > 0 && (localHead?.doc_status == 'order' || localHead?.doc_status == 'offert') && !hasUnsavedChanges" :disabled="localHead?.approval == 3" @click="openEmailModal">
            <v-icon name="hi-mail" class="mr-1" />
          </button>

          <!--Gruppo Approvazione-->
          <button v-if="forceAdminCorrection && localHead?.approval == 0 && localHead?.correctable == 0 && localHead?.doc_status == 'order'" 
            class="bg-yellow-500 text-white px-4 py-2 rounded hover:bg-yellow-600 disabled:bg-yellow-300"
            @click="requestApproval" :disabled="hasUnsavedChanges || localHead?.id <= 0">
            {{ $t('go.actions.request_approval') }}
          </button>

          <button v-if="forceAdminCorrection && localHead?.approval == 1 && $page.props.isAdminOrSuperAdmin"
            class="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 disabled:bg-green-300"
            @click="approveDocument" :disabled="hasUnsavedChanges">
            {{ $t('go.actions.approve_document') }}
          </button>

          <button v-if="forceAdminCorrection && localHead?.correctable == 0 && localHead?.approval != 1 && localHead?.doc_status == 'order'" 
            class="bg-yellow-500 text-white px-4 py-2 rounded hover:bg-yellow-600 disabled:bg-yellow-300"
            @click="requestCorrection" :disabled="hasUnsavedChanges || localHead?.id <= 0">
            {{ $t('go.actions.request_correction') }}
          </button>

        </div>
        <div class="text-sm text-right text-gray-600 space-y-1">
          <div>
            <span class="font-semibold text-gray-800">{{ $t('go.fields.created_by') }}&nbsp;</span>
            <span>{{ (localHead?.id === -1 ? $page.props.user?.name : owner?.name) || '—' }} {{ localHead?.created_at ? formatDateTime(localHead.created_at) : '—' }}</span>
          </div>
          <div>
            <span class="font-semibold text-gray-800">{{ $t('go.fields.last_modified_by') }}&nbsp;</span>
            <span>{{ (localHead?.id === -1 ? null : last_editor?.name) || '—' }} {{ localHead?.updated_at ? formatDateTime(localHead.updated_at) : '—' }}</span>
          </div>
        </div>
      </div>

      <!-- Order Details -->
      <div v-if="localHead" class="bg-white rounded shadow">
        <button
          type="button"
          @click="showOrderDetails = !showOrderDetails"
          class="w-full flex items-center justify-between p-4 text-left font-medium hover:bg-gray-50 focus:outline-none"
          :aria-expanded="showOrderDetails"
        >
          <span>
            Dettagli documento
            <span v-if="!showOrderDetails && localHead" class="ml-2 text-xs text-gray-500 font-normal">
              Numero: {{ localHead.doc_no || '—' }} del {{ localHead.doc_date?.toLocaleDateString?.('it-IT') || formatDate(localHead.doc_date) || '—' }} 
              - {{ localHead.customer_data?.CompanyName || '—' }} 
              - Agente: {{ salespeople?.find(s => s.SalesPerson === localHead.sales_person)?.Name || localHead.sales_person || '—' }} 
              - Pagamento: {{ localHead.payment_term?.Description || localHead.payment_term?.Payment || '—' }} 
              - Priorità: {{ localHead.priority || '0' }}
            </span>
          </span>
          <v-icon
            name="hi-chevron-down"
            scale="1.2"
            class="transition-transform duration-300 ease-out"
            :class="{ 'rotate-180': showOrderDetails }"
          />
        </button>
        <div
          class="grid transition-[grid-template-rows] duration-300 ease-out"
          :style="{ gridTemplateRows: showOrderDetails ? '1fr' : '0fr' }"
        >
          <div class="overflow-hidden">
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 border-t">
          <div>
            <label class="block text-sm font-medium">{{ $t('go.fields.number') }}</label>
            <input class="fullinput" v-model="localHead.doc_no" :disabled="$policy('numeroDocumento').isReadOnly || blockedEdits"/>
          </div>
          <div>
            <label class="block text-sm font-medium">{{ $t('go.fields.doc_type') }}</label>
            <select v-model="localHead.doc_status" class="fullinput" disabled>
              <option value="draft">{{ $t('go.doc_status.draft') }}</option>
              <option value="offert">{{ $t('go.doc_status.offert') }}</option>
              <option value="order">{{ $t('go.doc_status.order') }}</option>
            </select>
          </div>
          <div>
            <label class="block text-sm font-medium">{{ localHead?.is_contact == 1 ? $t('go.fields.contact') : $t('go.fields.customer') }}</label>
            <div class="relative w-full inline-flex items-center " @click="localHead?.is_contact == 1 ? showTableContact() : showTableCustomer()" :disabled="blockedEdits">
              <span class="clickinput"> {{ localHead?.customer_data?.CompanyName ?? $t('go.fields.select_placeholder') }}</span>
              <span class="absolute right-3 text-gray-500 pointer-events-none">
                <v-icon name="hi-arrows-expand" scale="1.2" />
              </span>
            </div>
            <p class="text-sm text-gray-500 mt-1" v-if="localHead?.customer_data">Codice: {{ localHead.is_contact == 1 ? localHead.customer_data?.Contact : localHead.customer_data?.CustSupp }} -
              {{ localHead.customer_data?.Address }}, {{ localHead.customer_data?.ZIPCode }} - {{ localHead.customer_data?.City }}({{
                localHead.customer_data?.County }}) - {{ localHead.customer_data?.Country }}</p>
            <button @click="caricaNoteCliente" class="text-sm text-blue-500 hover:underline mt-1" v-if="localHead.is_contact != 1 && localHead.customer_data?.CustSupp && localHead.customer_data.CustSupp != ''">{{ $t('go.actions.load_customer_notes') }}</button>
          </div>

          <div>
            <label class="block text-sm font-medium">{{ $t('go.fields.date') }}</label>
            <VueDatePicker v-model="localHead.doc_date" locale="it" format="dd/MM/yyyy" :placeholder="$t('ui.choose_date')"
              :enable-time-picker="false" class="border rounded-md" :teleport="true" auto-apply text-input required :disabled="blockedEdits">
            </VueDatePicker>
          </div>
          <div v-if="$policy('fineValidita').canView">
            <label class="block text-sm font-medium">Fine Validità</label>
            <VueDatePicker v-model="localHead.validity_ending_date" locale="it" format="dd/MM/yyyy"
              :placeholder="$t('ui.choose_date')" :enable-time-picker="false" class="border rounded-md" :teleport="true"
              auto-apply text-input :disabled="blockedEdits || $policy('fineValidita').props.disabled"></VueDatePicker>
          </div>
          <div v-if="$policy('dataConsegnaPrevista').canView">
            <label class="block text-sm font-medium">{{ $t('go.fields.expected_delivery') }} <span v-if="$policy('dataConsegnaPrevista').isRequired" class="text-red-500">*</span></label>
            <VueDatePicker v-model="localHead.expected_delivery_date" locale="it" format="dd/MM/yyyy"
              :placeholder="$t('ui.choose_date')" :enable-time-picker="false" class="border rounded-md" :teleport="true"
              auto-apply text-input :disabled="blockedEdits || $policy('dataConsegnaPrevista').props.disabled"></VueDatePicker>
          </div>

          <div v-if="localHead?.doc_status === 'order'">
            <label class="block text-sm font-medium">{{ $t('go.fields.mago_order_number') }}</label>
            <input class="fullinput" v-model="localHead.mago_doc_order_no" disabled />
          </div>
          <div v-else>
            <label class="block text-sm font-medium">{{ $t('go.fields.mago_quota_number') }}</label>
            <input class="fullinput" v-model="localHead.mago_doc_quota_no" disabled />
          </div>
          <div>
            <label class="block text-sm font-medium">{{ $t('go.fields.customer_order_ref') }}</label>
            <input class="fullinput" v-model="localHead.external_ref" :disabled="blockedEdits"/>
          </div>

          <div>
            <!-- Agente impostabile solo da admin, super admin, o capoarea (o da chi, per qualche motivo è dentro questa pagina senza agente associato) e solo se l'ordine non è ancora stato creato in Mago -->
            <label class="block text-sm font-medium">{{ $t('go.fields.agent') }}</label>
            <select v-model="localHead.sales_person" class="fullinput" :disabled="blockedEdits || (localHead?.mago_doc_order_no || (!$page.props.isAdminOrSuperAdmin && !$page.props.isCapoarea && $page.props.user.agent))">
              <option v-for="salesperson in salespeople" :key="salesperson.SalesPerson" :value="salesperson.SalesPerson" :disabled="salesperson.Disabled == 1">
                {{ salesperson.Name }} ({{ salesperson.SalesPerson }})
              </option>
            </select>
          </div>

          <div v-if="$policy('scontoCliente').canView">
            <label class="block text-sm font-medium">{{ $t('go.fields.customer_discount') }}</label>
            <div class="fullinput" disabled>{{ localHead.customer_data?.DiscountFormula }}&nbsp;</div>
          </div>
          <div>
            <label class="block text-sm font-medium">{{ $t('go.fields.payment') }}</label>
            <div class="relative w-full inline-flex items-center " @click="showTablePayments" :disabled="blockedEdits">
              <span class="clickinput"> {{ localHead.payment_term?.Payment }} - {{ localHead.payment_term?.Description }}</span>
              <span class="absolute right-3 text-gray-500 pointer-events-none">
                <v-icon name="hi-arrows-expand" scale="1.2" />
              </span>
            </div>
            <p class="text-sm text-gray-500 mt-1" v-if="localHead.payment_term && parseFloat(localHead.payment_term.DiscountFormula) > 0">
              {{ $t('go.fields.extra_discount_default') }} {{localHead.payment_term.DiscountFormula }}
            </p>
          </div>
          <div>
            <label class="block text-sm font-medium">{{ $t('go.fields.priority') }}</label>
            <input class="fullinput" type="number" v-model="localHead.priority" min="-32768" max="32767" :disabled="blockedEdits"/>
          </div>
          <div v-if="$policy('privateNote').canView" class="md:col-span-3">
            <textarea v-model="localHead.private_note" class="fullinput" rows="4" :placeholder="$t('go.private_note_placeholder')" :disabled="blockedEdits || $policy('privateNote').props.disabled"></textarea>
          </div>
          <!--<div>
              <label class="block text-sm font-medium">Riferimento</label>
              <input class="fullinput"v-model="localHead.riferimento"  />
          </div>-->
            </div>
          </div>
        </div>
      </div>

      <!-- Tabs -->
      <div class="flex space-x-4 border-b text-sm font-medium">
        <div v-for="tab in tabs" :key="tab" @click="selectedTab = tab.value" class="pb-2 cursor-pointer" :class="{
          'border-b-2 border-blue-500 text-black': selectedTab === tab.value,
          'text-gray-500': selectedTab !== tab.value
        }">
          {{ tab.name }}
        </div>

      </div>

      <!-- Articoli -->
      <div class="bg-white p-4 rounded shadow overflow-x-auto" v-if="selectedTab === 'main'">
        <button @click="articleServiceFilter = 0; loadArticleTable(undefined, undefined, undefined)"
          class="bg-blue-400 disabled:bg-blue-300 text-white px-4 py-2 rounded hover:bg-blue-500" :disabled="blockedEdits">
          {{ $t('go.actions.add_goods') }}
        </button>&nbsp;
        <template v-if="$policy('addService').canView">
          <button
            @click="articleServiceFilter = 1; loadArticleTable(undefined, undefined, undefined)"
            class="bg-orange-400 disabled:bg-orange-300 text-white px-4 py-2 rounded hover:bg-orange-500"
            :disabled="blockedEdits || $policy('addService').props.disabled"
            >
            {{ $t('go.actions.add_service') }}
          </button>&nbsp;
        </template>
        <template v-if="$policy('addNota').canView">
          <button
            @click="openNotePanel()"
            class="bg-gray-400 disabled:bg-gray-300 text-white px-4 py-2 rounded hover:bg-gray-500"
            :disabled="blockedEdits || $policy('addNota').props.disabled"
            >
            {{ $t('go.actions.add_note') }}
          </button>&nbsp;
        </template>
        <template v-if="$policy('addDescrittiva').canView">
          <button
            @click="articleServiceFilter = 2; loadArticleTable(undefined, undefined, undefined)"
            class="bg-pink-400 disabled:bg-pink-300 text-white px-4 py-2 rounded hover:bg-pink-500"
            :disabled="blockedEdits || $policy('addDescrittiva').props.disabled"
            >
            {{ $t('go.actions.add_descriptive') }}
          </button>&nbsp;
        </template>
        <template v-if="$policy('addFuoriMagazzino').canView">
          <button
          @click="fuoriMagazzino()"
            class="bg-green-400 disabled:bg-green-300 text-white px-4 py-2 rounded hover:bg-green-500"
            :disabled="blockedEdits || $policy('addFuoriMagazzino').props.disabled"
            >
            {{ $t('ui.out_of_inventory') }}
          </button>
        </template>
        <div class="mt-4">
          <PageNormalTable :headers="detailHeaders" :items="localDetails" hide-default-footer hover>
            <template #item.position="{ item }">
              <div class="flex items-center gap-2">
                <div class="flex flex-col items-center gap-1">
                  <button v-if="!isKitChild(item) && !isFirstItem(item) && !blockedEdits" @click="moveItemUp(item)"
                    class="text-blue-500 hover:text-blue-700 p-1" title="Sposta su">
                    ▲
                  </button>
                  <button v-if="!isKitChild(item) && !isLastItem(item) && !blockedEdits" @click="moveItemDown(item)"
                    class="text-blue-500 hover:text-blue-700 p-1" title="Sposta giù">
                    ▼
                  </button>
                </div>
                <span class="flex items-center">{{ getDisplayPosition(item) }}</span>
              </div>
            </template>

            <template #item.line_type="{ item }">
              <span>{{ item.line_type == MagoEnums.Line_Type.Goods ? 'Merce' : item.line_type == MagoEnums.Line_Type.Service ? 'Servizio' : item.line_type == MagoEnums.Line_Type.Descriptive ? 'Descrittiva' : item.line_type
                == MagoEnums.Line_Type.Notes ? 'Nota' : item.line_type }}</span>
            </template>

            <template #item.status="{ item }">
              <div class="flex items-center gap-2">
                <template v-if="getRowAvailabilityStatus(item) === 'green'">
                  <div class="w-4 h-4 rounded-sm bg-green-500" :title="$t('go.availability.available')"></div>
                </template>
                <template v-else-if="getRowAvailabilityStatus(item) === 'yellow'">
                  <div class="w-4 h-4 rounded-sm bg-yellow-500" :title="$t('go.availability.not_available_with_orders')"></div>
                </template>
                <template v-else-if="getRowAvailabilityStatus(item) === 'red'">
                  <div class="w-4 h-4 rounded-sm bg-red-500" :title="$t('go.availability.not_available')"></div>
                </template>
                <template v-else-if="getRowAvailabilityStatus(item) === 'none'">
                  <div class="w-4 h-4 rounded-sm bg-gray-300" :title="$t('go.availability.no_info')"></div>
                </template>
                <template v-if="personalizzazioniImel">
                  <span class="text-xs">
                    {{ getItemLocation(item) }}
                  </span>
                </template>
                <template v-if="item.exclude_from_print" >
                  <div class="relative inline-block">
                    <v-icon name="co-print" class="text-gray-500 line-through" scale="1.2" :title="$t('go.fields.exclude_print')"/>
                    <div class="absolute w-[100%] h-[2px] bg-red-500 -rotate-45 top-1/2"></div>
                  </div>      
                </template>
                <template v-if="item.exclude_from_invoice" >
                  <div class="relative inline-block">
                    <v-icon name="la-file-invoice-dollar-solid" class="text-gray-500 line-through" scale="1.2" :title="$t('go.fields.exclude_invoice')"/>
                    <div class="absolute w-[100%] h-[2px] bg-red-500 -rotate-45 top-1/2"></div>
                  </div>                 
                </template>
                <template v-if="item.exclude_from_ddt" >
                  <div class="relative inline-block">
                    <v-icon name="la-file-invoice-solid" class="text-gray-500 line-through" scale="1.2" :title="$t('go.fields.exclude_ddt')"/>
                    <div class="absolute w-[100%] h-[2px] bg-red-500 -rotate-45 top-1/2"></div>
                  </div>        
                </template>
              </div>
            </template>

            <template #item.qty="{ item }">
              <span>{{ formatNumberDisplay(item.qty, quantityDecimals, useThousandSeparator) }}</span>
            </template>

            <template #item.unit_value="{ item }">
              <span class="whitespace-nowrap">{{ formatNumberDisplay(item.unit_value, unitValueDecimals, useThousandSeparator) }} €</span>
              <p v-if="item.unit_value == 0 && allowPriceRowZero" class="whitespace-pre">Da determinare</p>
            </template>

            <template #item.taxable_amount="{ item }">
              <span class="whitespace-nowrap">{{ formatNumberDisplay(item.taxable_amount, 2, useThousandSeparator) }} €</span>
            </template>

            <template #item.nota="{ item }">
              <span class="block whitespace-pre-line" :style="{ 'min-width': rowTextWidth('nota') }">{{ item.nota }}</span>
            </template>

            <template #item.description="{ item }">
              <span class="block whitespace-pre-line" :style="{ 'min-width': rowTextWidth('description') }">{{ item.description }}</span>
            </template>

            <template #item.total_amount="{ item }">
              <span class="whitespace-nowrap">{{ formatNumberDisplay(item.total_amount + item.weee_amount * (item.weee_tax_percentage / 100 + 1), 2, useThousandSeparator) }} €</span>
            </template>

            <template #item.actions="{ item }">
              <div class="flex justify-center space-x-2 text-xl">
                <template v-if="!isKitChild(item)">
                  <v-icon name="fa-edit" class="text-blue-500 hover:text-blue-700" @click="isKitParent(item) ? editKitDetail(item) : editDetail(item)" scale="1.3" v-if="!blockedEdits"></v-icon>
                  <v-icon name="fa-eye" class="text-blue-500 hover:text-blue-700" @click="editDetail(item)" scale="1.3" v-if="blockedEdits"></v-icon>
                  <v-icon name="fa-trash" class="text-red-500 hover:text-red-700" @click="toDelete = item; showDeleteModalRow = true;" scale="1.3" v-if="!blockedEdits"></v-icon>
                </template>
              </div>
            </template>
          </PageNormalTable>
        </div>
      </div>

      <div v-if="selectedTab === 'charges' && $policy('tabSpese').canView">
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4 bg-white p-4 rounded shadow">
          <div v-if="$policy('speseTrasporto').canView">
            <label class="block">{{ $t('go.fields.transport_charges') }}</label>
            <div class="relative w-full inline-flex items-center">
              <input class="margininput" type="text" :value="formatNumberForInput(localHead.transport_price, 2, useThousandSeparator)" @blur="e => localHead.transport_price = parseNumberFromInput(e.target.value, 2)" min="0" max="100" :disabled="blockedEdits || $policy('speseTrasporto').props.disabled"/>
              <span class="absolute right-3 text-gray-500 pointer-events-none">€</span>
            </div>
          </div>
          <div v-if="$policy('speseIncasso').canView">
            <label class="block">{{ $t('go.fields.collection_charges') }}</label>
            <div class="grid grid-cols-2 gap-2">
              <div>
                <div class="relative w-full inline-flex items-center">
                  <input class="margininput" type="text" :value="formatNumberForInput(localHead.collection_charges_price, 2, useThousandSeparator)" @blur="e => localHead.collection_charges_price = parseNumberFromInput(e.target.value, 2)" min="0" max="100" :disabled="blockedEdits || $policy('speseIncasso').props.disabled"/>
                  <span class="absolute right-3 text-gray-500 pointer-events-none">€</span>
                </div>
              </div>
              <div>
                <div class="relative w-full inline-flex items-center " @click="showTableCollectionTaxCode" :disabled="blockedEdits || $policy('speseIncasso').props.disabled">
                  <span class="clickinput"> {{ $t('go.fields.cod_iva_label') }} {{ localHead.collection_tax_code }}</span>
                  <span class="absolute right-3 text-gray-500 pointer-events-none">
                    <v-icon name="hi-arrows-expand" scale="1.2" />
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div v-if="$policy('speseAccessorie').canView">
            <label class="block">{{ $t('go.fields.accessory_charges') }}</label>
            <div class="relative w-full inline-flex items-center">
              <input class="margininput" type="text" :value="formatNumberForInput(localHead.other_price, 2, useThousandSeparator)" @blur="e => localHead.other_price = parseNumberFromInput(e.target.value, 2)" min="0" max="100" :disabled="blockedEdits || $policy('speseAccessorie').props.disabled"/>
              <span class="absolute right-3 text-gray-500 pointer-events-none">€</span>
            </div>
          </div>
        </div>
      </div>

      <div v-if="selectedTab === 'documentnotes'">
        <textarea v-model="localHead.additional_note" class="fullinput" rows="4" :placeholder="$t('go.document_notes_placeholder')" :disabled="blockedEdits"></textarea>

        <div v-if="enableOrderPeopleSelection && localHead.is_contact != 1" class="mt-4">
          <label class="block mb-1">Persone collegate</label>
          <v-select
            :model-value="linkedPeopleSelection"
            @update:model-value="updateLinkedPeople"
            :options="peopleOptions"
            label="label"
            :reduce="option => option.value"
            multiple
            placeholder="Seleziona una o più persone"
            :disabled="blockedEdits"
          />
        </div>
      </div>

      <div v-if="selectedTab === 'shipping' && $policy('tabSpedizione').canView" class="bg-white p-4 rounded shadow grid grid-cols-2 gap-4">
        <div v-if="$policy('sedeConsegna').canView">
          <label class="block">{{ $t('go.fields.delivery_branch') }}</label>
          <select v-model="localHead.delivery_branch" class="fullinput" :disabled="blockedEdits || $policy('sedeConsegna').props.disabled">
            <option :value="null">{{ $t('go.fields.generic_branch') }}</option>
            <option v-for="branch in branches" :value="branch.Branch" :key="branch.Branch" :selected="branch.Branch === head.delivery_branch">
              {{ branch.Branch }} {{ branch.CompanyName }} - {{ branch.Address }} - {{ branch.ZIPCode }} {{ branch.City }} ({{ branch.County }}) - {{ branch.Country }}
            </option>
          </select>
          <!-- Display selected branch info if available -->
          <p class="text-sm text-gray-500 mt-1" v-if="head.delivery_branch && head.delivery_branch.Branch">
            {{ $t('go.fields.selected_branch') }} {{ head.delivery_branch.Branch }} - {{ head.delivery_branch.CompanyName }}
          </p>
        </div>
        <div v-if="$policy('primoVettore').canView">
          <label class="block">{{ $t('go.fields.carrier') }}</label>
          <select v-model="localHead.carrier1" class="fullinput" :disabled="blockedEdits || $policy('primoVettore').props.disabled">
            <option :value="null">{{ $t('go.fields.select_carrier') }}</option>
            <option v-for="carrier in carriers" :value="carrier.Carrier" :disabled="carrier.Disabled == 1">
              {{ carrier.Carrier }} - {{ carrier.CompanyName }}
            </option>
          </select>
        </div>
      </div>

      <!-- Totali -->
      <div class="bg-white p-4 rounded shadow grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 text-sm">
        <div v-if="$policy('ulterioreSconto').canView">
          <label class="block">{{$t('ui.ulteriore_sconto')}}</label>
          <div class="relative w-full inline-flex items-center">
            <input class="margininput" type="text" maxlength="16" :value="formatNumberForInput(localHead.discount_formula, 2, useThousandSeparator)" @blur="e => localHead.discount_formula = parseNumberFromInput(e.target.value, 2)" @keypress="e => { if (!/[0-9+\-.]/.test(e.key)) e.preventDefault() }" :disabled="blockedEdits || $policy('ulterioreSconto').props.disabled"/>
            <span class="absolute right-3 text-gray-500 pointer-events-none">%</span>
          </div>
        </div>
        <div v-if="$policy('importoAbbuono').canView">
          <label class="block">{{ $t('go.fields.allowances') }}</label>
          <div class="relative w-full inline-flex items-center">
            <input class="margininput" type="text" :value="formatNumberForInput(localHead.allowances, 2, useThousandSeparator)" @blur="e => localHead.allowances = parseNumberFromInput(e.target.value, 2)" :disabled="blockedEdits || $policy('importoAbbuono').props.disabled"/>
            <span class="absolute right-3 text-gray-500 pointer-events-none">€</span>
          </div>
        </div>
        <div>
          <label class="block">{{ $t('go.fields.goods_total') }}</label>
          <div class="relative w-full inline-flex items-center">
            <div class="margininput" disabled>{{ formatNumberDisplay(orderTotals.goods_total, 2, useThousandSeparator) }}</div>
            <span class="absolute right-3 text-gray-500 pointer-events-none">€</span>
          </div>
        </div>
        <div>
          <label class="block">{{ $t('go.fields.services_total') }}</label>
          <div class="relative w-full inline-flex items-center">
            <div class="margininput" disabled>{{ formatNumberDisplay(orderTotals.services_total, 2, useThousandSeparator) }}</div>
            <span class="absolute right-3 text-gray-500 pointer-events-none">€</span>
          </div>
        </div>
        <div v-if="handleWeee">
          <label class="block">Totale RAEE</label>
          <div class="relative w-full inline-flex items-center">
            <div class="margininput" disabled>{{ formatNumberDisplay(orderTotals.total_weee_amount, 2, useThousandSeparator) }}</div>
            <span class="absolute right-3 text-gray-500 pointer-events-none">€</span>
          </div>
        </div>
        <div>
          <label class="block">{{ $t('go.fields.discount_total') }}</label>
          <div class="relative w-full inline-flex items-center">
            <div class="margininput" disabled>{{ formatNumberDisplay(orderTotals.discount_amount, 2, useThousandSeparator) }}</div>
            <span class="absolute right-3 text-gray-500 pointer-events-none">€</span>
          </div>
        </div>
        <div>
          <label class="block">{{ $t('go.fields.taxable') }}</label>
          <div class="relative w-full inline-flex items-center">
            <div class="margininput" disabled>{{ formatNumberDisplay(orderTotals.taxable_amount, 2, useThousandSeparator) }}</div>
            <span class="absolute right-3 text-gray-500 pointer-events-none">€</span>
          </div>
        </div>
        <div>
          <label class="block">{{ $t('go.fields.vat_total') }}</label>
          <div class="relative w-full inline-flex items-center">
            <div class="margininput" disabled>{{ formatNumberDisplay(orderTotals.tax_amount, 2, useThousandSeparator) }}</div>
            <span class="absolute right-3 text-gray-500 pointer-events-none">€</span>
          </div>
        </div>
        <div>
          <label class="block">{{ $t('go.fields.document_total') }}</label>
          <div class="relative w-full inline-flex items-center">
            <div class="margininput" disabled>{{ formatNumberDisplay(vatBreakdownTotals.total, 2, useThousandSeparator) }}</div>
            <span class="absolute right-3 text-gray-500 pointer-events-none">€</span>
          </div>
        </div>
        <div v-if="$policy('totaleOmaggi').canView">
          <label class="block">Totale omaggi</label>
          <div class="relative w-full inline-flex items-center">
            <div class="margininput" disabled>{{ formatNumberDisplay(orderTotals.total_freesamples_amount, 2, useThousandSeparator) }}</div>
            <span class="absolute right-3 text-gray-500 pointer-events-none">€</span>
          </div>
        </div>
        <div>
          <label class="block">{{ $t('go.fields.to_pay') }}</label>
          <div class="relative w-full inline-flex items-center">
            <div class="margininput" disabled>{{ formatNumberDisplay(orderTotals.total_amount, 2, useThousandSeparator) }}</div>
            <span class="absolute right-3 text-gray-500 pointer-events-none">€</span>
          </div>
        </div>
      </div>

      <!-- Castelletto IVA espandibile -->
      <div ref="vatBreakdownSection" class="bg-white rounded shadow overflow-hidden">
        <button 
          @click="toggleVatBreakdown"
          class="w-full p-4 flex items-center justify-between text-left font-medium text-gray-700 hover:bg-gray-50 transition-colors"
        >
          <span class="text-sm">{{ $t('go.fields.vat_breakdown') }}</span>
          <svg 
            class="w-5 h-5 transition-transform duration-200"
            :class="{ 'rotate-180': showVatBreakdown }"
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
          </svg>
        </button>
        
        <div 
          v-show="showVatBreakdown"
          class="border-t border-gray-200"
        >
          <div class="p-4 overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50">
                <tr>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {{ $t('go.fields.tax_code_full') }}
                  </th>
                  <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {{ $t('go.fields.taxable') }}
                  </th>
                  <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {{ $t('go.fields.tax_amount') }}
                  </th>
                  <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {{ $t('go.fields.total') }}
                  </th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr v-for="(item, index) in orderTotals.vat_breakdown" :key="index" class="hover:bg-gray-50">
                  <td class="px-4 py-3 text-sm text-gray-900">
                    {{ item.tax_code }}
                  </td>
                  <td class="px-4 py-3 text-sm text-gray-900 text-right">
                    {{ formatNumberDisplay(item.taxable, 2, useThousandSeparator) }}
                  </td>
                  <td class="px-4 py-3 text-sm text-gray-900 text-right">
                    {{ formatNumberDisplay(item.tax, 2, useThousandSeparator) }}
                  </td>
                  <td class="px-4 py-3 text-sm text-gray-900 text-right">
                    {{ formatNumberDisplay(item.total, 2, useThousandSeparator) }}
                  </td>
                </tr>
                <tr v-if="orderTotals.vat_breakdown && orderTotals.vat_breakdown.length > 0" class="bg-gray-100 font-bold border-t-2 border-gray-300">
                  <td class="px-4 py-3 text-sm text-gray-900">
                    {{ $t('go.vat_breakdown.total_row') }}
                  </td>
                  <td class="px-4 py-3 text-sm text-gray-900 text-right">
                    {{ formatNumberDisplay(vatBreakdownTotals.taxable, 2, useThousandSeparator) }}
                  </td>
                  <td class="px-4 py-3 text-sm text-gray-900 text-right">
                    {{ formatNumberDisplay(vatBreakdownTotals.tax, 2, useThousandSeparator) }}
                  </td>
                  <td class="px-4 py-3 text-sm text-gray-900 text-right">
                    {{ formatNumberDisplay(vatBreakdownTotals.total, 2, useThousandSeparator) }}
                  </td>
                </tr>
                <tr v-if="!orderTotals.vat_breakdown || orderTotals.vat_breakdown.length === 0">
                  <td colspan="4" class="px-4 py-3 text-sm text-gray-500 text-center">
                    {{ $t('go.vat_breakdown.no_details') }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <div class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 pt-16"
      v-if="editDetailModal">


      <div class="bg-white w-full max-w-4xl rounded-2xl shadow-xl overflow-y-auto max-h-[90vh]">
        <div class="px-6 py-4 border-b flex items-center justify-between bg-gray-50">
            <h2 class="text-xl font-bold text-gray-800 flex items-center gap-2">
              <v-icon name="bi-pencil" class="text-blue-600" scale="1.2" />
              {{ $t('go.modals.detail_modal_title') }} {{ detailData.custom ? $t('go.modals.out_of_stock_label') : '' }}
            </h2>
        </div>
        <div class="p-6 pt-3">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label class="block">{{ $t('go.fields.row_type') }}</label>
              <select class="fullinput" v-model="detailData.line_type" :disabled="!detailData.custom">
                <option v-if="!detailData.custom" :value="MagoEnums.Line_Type.Notes">{{ $t('go.line_types.note') }}</option>
                <option :value="MagoEnums.Line_Type.Service">{{ $t('go.line_types.service') }}</option>
                <option :value="MagoEnums.Line_Type.Goods">{{ $t('go.line_types.goods') }}</option>
                <option v-if="!detailData.custom" :value="MagoEnums.Line_Type.Descriptive">{{ $t('go.line_types.descriptive') }}</option>
              </select>
            </div>

            <div v-if="detailData.line_type != MagoEnums.Line_Type.Notes">
              <label class="block">{{ $t('go.fields.item_article') }}:</label>
              <div class="relative w-full inline-flex items-center">
                <input class="margininput" v-model="detailData.item" :disabled="!detailData.custom" />
                <span class="absolute right-3">
                <button v-if="!detailData.custom" @click="getArticleInfo(detailData.item)" class="hover:text-blue-500">
                  <v-icon name="bi-info-circle" scale="1.2" />
                </button>
              </span>
              </div>
            </div>

            <div class="md:col-span-2" v-if="currentItemNotes.length > 0 && detailData.line_type != MagoEnums.Line_Type.Notes">
              <label class="block text-sm font-medium text-amber-800 flex items-center mb-1">
                <v-icon name="bi-info-circle" class="mr-1" scale="0.9" />
                {{ $t('go.fields.item_notes') }}
              </label>
              <div class="bg-amber-50 border-l-4 border-amber-400 p-3 rounded-r-lg shadow-sm">
                <ul class="list-disc list-inside space-y-1">
                  <li v-for="note in currentItemNotes" :key="note.Notes" class="text-sm text-amber-900 font-medium italic">
                    {{ note.Notes }}
                  </li>
                </ul>
              </div>
            </div>

            <div class="md:col-span-2">
              <label class="block">{{ $t('go.fields.description') }}</label>
              <textarea rows="2" class="fullinput" v-model="detailData.description" :disabled="(detailData.line_type != MagoEnums.Line_Type.Notes) && (!detailData.custom)" maxlength="255"></textarea>
            </div>

            <!--<div class="md:col-span-2"> Rimasuglio portale vecchio probabilmente
              <label class="block">Descrizione aggiuntiva</label>
              <textarea rows="2" class="fullinput" v-model="detailData.additional_description"></textarea>
            </div>-->
          </div>

          <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4" v-if="detailData.line_type != MagoEnums.Line_Type.Notes">
            <div>
              <label class="block text-sm font-medium text-gray-700">{{ $t('go.fields.sale_type') }}</label>
              <select v-model="detailData.sale_type" class="fullinput" :disabled="blockedEdits">
                <option :value="MagoEnums.Sale_Type.Normal">{{ $t('go.fields.sale_type_normal') }}</option>
                <option :value="MagoEnums.Sale_Type.Total_Free_Sample">{{ $t('go.fields.sale_type_total_free_sample') }}</option>
                <option :value="MagoEnums.Sale_Type.Taxable_Free_Sample">{{ $t('go.fields.sale_type_taxable_free_sample') }}</option>
              </select>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 flex items-center gap-1">{{ $t('go.fields.pricelist') }} <v-icon name="bi-info-circle" scale="0.9" title="In assenza di listino specificato, verrà usata la politica prezzi di Mago." /></label>
              <div v-if="showPricelistInfo && detailData.item && !detailData.custom" class="relative w-full inline-flex items-center " @click="openPricelistInfo(detailData.item)" :disabled="blockedEdits" >
                  <span class="clickinput truncate">{{ detailData.price_list }} - {{ pricelists.find(x => x.PriceList === detailData.price_list)?.Description }}</span>
                  <span class="absolute right-3 text-gray-500">
                    <v-icon name="hi-arrows-expand" scale="1.2" />
                  </span>
              </div>
              <!-- Ricalcola cambio listino solo con modifica utente, senza watch -->
              <select v-else-if="$policy('selettoreListino').canView" v-model="detailData.price_list" class="fullinput" @change="calculateRowTotalsFromServer('price_list')" :disabled="blockedEdits || $policy('selettoreListino').props.disabled" >
                <option value="">{{ $t('go.fields.none_specified') }}</option>
                <option v-for="pricelist in pricelists" :value="pricelist.PriceList" :disabled="pricelist.AlwaysShow != 1 && pricelist.PriceList != localHead.customer_data?.PriceList && !detailData.custom || (handleKits && pricelist.PkListinoKit == 1)">
                  {{ pricelist.PriceList }} - {{ pricelist.Description }}
                </option>
              </select>
            </div>

            <div v-if="packageMode">
              <label class="block text-sm font-medium text-gray-700">{{ $t('go.fields.packages') }} ({{ detailData.package_uom ?? '' }})</label>
              <div class="relative w-full inline-flex items-center">
                <input class="fullinput" type="number" step="1" min="0" v-model.number="packageQty" @blur="onPackageQtyChange" :disabled="blockedEdits"/>
              </div>
              <span class="text-red-500 mt-1 text-sm"> {{ uomText(detailData.package_uom) }}</span>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700">{{ $t('go.fields.qty') }}{{ packageMode ? ' ('+ detailData.actual_uom + ' - ' +  available_uoms.find(u => u.ComparableUoM == detailData.actual_uom)?.Description +')' : '' }}</label>
              <div class="relative w-full inline-flex items-center">
                <input class="fullinput" type="text" :value="formatNumberForInput(detailData.qty, quantityDecimals, useThousandSeparator)" @blur="e => { detailData.qty = parseNumberFromInput(e.target.value, quantityDecimals); calculateRowTotalsFromServer('qty'); }" min="0" :disabled="blockedEdits || packageMode"/>
              </div>
            </div>

            <div v-if="!packageMode">
              <label class="block text-sm font-medium text-gray-700">{{ $t('go.fields.uom') }}</label>
              <!-- Ricalcola cambio listino solo con modifica utente, senza watch -->
              <select v-model="detailData.actual_uom" class="fullinput" @change="calculateRowTotalsFromServer('uom')" :disabled="blockedEdits">
                <template v-if="!detailData.custom">
                  <option v-for="uom in available_uoms" :value="uom.ComparableUoM" :key="uom.ComparableUoM">
                    {{ uom.ComparableUoM }} - {{ uom.Description }}
                  </option>
                </template>
                <template v-else>
                  <option v-for="uom in available_uoms" :value="uom.id" :key="uom.id" :selected="uom.id === detailData.actual_uom">
                    {{ uom.id }} - {{ uom.name }}
                  </option>
                </template>
              </select>
              <span v-if="detailData.actual_uom != detailData.base_uom" class="text-red-500 mt-1 text-sm"> {{ uomText() }}</span>
            </div>

            <div v-if="$policy('inputValoreUnitario').canView">
              <label class="block text-sm font-medium text-gray-700">{{ $t('go.fields.unit_value') }}{{ packageMode ? ' ('+ detailData.actual_uom + ' - ' +  available_uoms.find(u => u.ComparableUoM == detailData.actual_uom)?.Description +')' : '' }}</label>
              <div class="relative w-full inline-flex items-center">
                <input class="margininput" type="text" :value="formatNumberForInput(detailData.unit_value, unitValueDecimals, useThousandSeparator)" @blur="e => { detailData.unit_value = parseNumberFromInput(e.target.value, unitValueDecimals); calculateRowTotalsFromServer('unit_value'); }" min="0" :disabled="blockedEdits || $policy('inputValoreUnitario').props.disabled"/>
                <span class="absolute right-3 text-gray-500 pointer-events-none whitespace-nowrap">€</span>
              </div>
              <p class="text-sm text-gray-500 mt-1" v-if="detailData.unit_value == 0 && allowPriceRowZero">{{ $t('go.fields.to_determine') }}</p>
            </div>

            <div v-if="$policy('inputSconto').canView">
              <label class="block text-sm font-medium text-gray-700">{{ $t('go.fields.discount') }} <v-icon name="bi-info-circle" class="mr-1" scale="0.9" title="Verrà assegnato di default lo sconto secondo le politiche di Mago." /></label>
              <div class="relative w-full inline-flex items-center">
                <input class="margininput" type="text" maxlength="16" v-model="detailData.discount_formula" @change="calculateRowTotalsFromServer('discount_formula')" @keypress="e => { if (!/[0-9+\-.]/.test(e.key)) e.preventDefault() }" :disabled="blockedEdits || $policy('inputSconto').props.disabled"/>
                <span class="absolute right-3 text-gray-500 pointer-events-none">%</span>
              </div>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700">{{ $t('go.fields.net_price') }}</label>
              <div class="relative w-full inline-flex items-center">
                <div class="margininput" disabled>{{ formatNumberDisplay(detailData.net_price, 2, useThousandSeparator) }}</div>
                <span class="absolute right-3 text-gray-500 pointer-events-none whitespace-nowrap">€</span>
              </div>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700">{{ $t('go.fields.tax_code_iva') }}</label>
              <div class="relative w-full inline-flex items-center " @click="showTableTaxCode" :disabled="blockedEdits">
                <span class="clickinput"> {{ detailData.tax_code }}</span>
                <span class="absolute right-3 text-gray-500 pointer-events-none">
                  <v-icon name="hi-arrows-expand" scale="1.2" />
                </span>
              </div>
            </div>
          
          </div>
          <div class="grid grid-cols-1 gap-4 mb-4" v-if="detailData.line_type != MagoEnums.Line_Type.Notes" :class="{ 'md:grid-cols-4': !handleWeee, 'md:grid-cols-5': handleWeee }">

            <div>
              <label class="block text-sm font-medium text-gray-700">{{ $t('go.fields.taxable_total') }}</label>
              <div class="relative w-full inline-flex items-center">
                <div class="margininput" disabled> {{ formatNumberDisplay(detailData.taxable_amount, 2, useThousandSeparator) }}</div>
                <span class="absolute right-3 text-gray-500 pointer-events-none whitespace-nowrap">€</span>
              </div>
            </div>

            <div v-if="handleWeee">
              <label class="block text-sm font-medium text-gray-700">Imp. RAEE<span v-if="detailData.weee_ctg"> - {{ detailData.weee_ctg }} - {{ detailData.weee_tax_code }}</span></label>
              <div class="relative w-full inline-flex items-center">
                <div class="margininput" disabled> {{ formatNumberDisplay(detailData.weee_amount, 2, useThousandSeparator) }}</div>
                <span class="absolute right-3 text-gray-500 pointer-events-none whitespace-nowrap">€</span>
              </div>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700">{{ $t('go.fields.vat_percentage') }}</label>
              <div class="relative w-full inline-flex items-center">
                <input class="margininput" type="number" v-model="detailData.tax_percentage" disabled />
                <span class="absolute right-3 text-gray-500 pointer-events-none">%</span>
              </div>
            </div>
            <!-- I valori RAEE vengono aggiunti da frontend perché altrimenti sarebbero calcolati due volte in backend, unica eccezione -->
            <div>
              <label class="block text-sm font-medium text-gray-700">Importo IVA</label>
              <div class="relative w-full inline-flex items-center">
                <div class="margininput" disabled> {{ formatNumberDisplay(detailData.tax_amount + detailData.weee_amount * detailData.weee_tax_percentage / 100, 2, useThousandSeparator) }}</div>
                <span class="absolute right-3 text-gray-500 pointer-events-none whitespace-nowrap">€</span>
              </div>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700">Totale Riga (IVA inclusa)</label>
              <div class="relative w-full inline-flex items-center">
                <div class="margininput" disabled> {{ formatNumberDisplay(detailData.total_amount + detailData.weee_amount * (detailData.weee_tax_percentage / 100 + 1), 2, useThousandSeparator) }}</div>
                <span class="absolute right-3 text-gray-500 pointer-events-none whitespace-nowrap">€</span>
              </div>
            </div>
          </div>

          <!-- Mago tecnicamente ti lascia aggiungere la colonna nota alla riga nota, ma il cliente potrebbe trovarlo confusionario -->
          <div class="md:col-span-2" v-if="detailData.line_type != MagoEnums.Line_Type.Notes || showNotesOnNotesRows">
              <label class="block">{{ $t('go.fields.row_note') }}</label>
              <textarea rows="2" class="fullinput" v-model="detailData.nota" :disabled="blockedEdits"></textarea>
          </div>

          <div class="mb-4" v-if="detailData.line_type != MagoEnums.Line_Type.Notes">
            <label class="block">{{ $t('go.fields.expected_delivery') }}</label>
            <VueDatePicker v-model="detailData.expected_delivery_date" locale="it" format="dd/MM/yyyy"
              :placeholder="$t('ui.choose_date')" :enable-time-picker="false" class="border rounded-md" :teleport="true"
              auto-apply text-input :disabled="blockedEdits"></VueDatePicker>
          </div>

          <div class="flex flex-wrap gap-6 mt-4 mb-2">
            <label class="flex items-center cursor-pointer gap-3" v-if="$policy('excludePrint').canView">
              <div class="relative w-10 h-5 bg-gray-300 rounded-full transition-colors duration-200"
                :class="{ 'bg-red-500': detailData.exclude_from_print }">
                <span
                  class="absolute left-0 top-0 h-5 w-5 bg-white rounded-full shadow transform transition-transform duration-200"
                  :class="{ 'translate-x-5': detailData.exclude_from_print }" />
                <input type="checkbox" v-model="detailData.exclude_from_print" class="sr-only" :disabled="blockedEdits" />
              </div>
              <span class="text-sm text-gray-700">{{ $t('go.fields.exclude_print') }}</span>
            </label>
            <label class="flex items-center cursor-pointer gap-3" v-if="$policy('excludeInvoice').canView">
              <div class="relative w-10 h-5 bg-gray-300 rounded-full transition-colors duration-200"
                :class="{ 'bg-red-500': detailData.exclude_from_invoice }">
                <span
                  class="absolute left-0 top-0 h-5 w-5 bg-white rounded-full shadow transform transition-transform duration-200"
                  :class="{ 'translate-x-5': detailData.exclude_from_invoice }" />
                <input type="checkbox" v-model="detailData.exclude_from_invoice" class="sr-only" :disabled="blockedEdits" />
              </div>
              <span class="text-sm text-gray-700">{{ $t('go.fields.exclude_invoice') }}</span>
            </label>
            <label class="flex items-center cursor-pointer gap-3" v-if="$policy('excludeDDT').canView">
              <div class="relative w-10 h-5 bg-gray-300 rounded-full transition-colors duration-200"
                :class="{ 'bg-red-500': detailData.exclude_from_ddt }">
                <span
                  class="absolute left-0 top-0 h-5 w-5 bg-white rounded-full shadow transform transition-transform duration-200"
                  :class="{ 'translate-x-5': detailData.exclude_from_ddt }" />
                <input type="checkbox" v-model="detailData.exclude_from_ddt" class="sr-only" :disabled="blockedEdits" />
              </div>
              <span class="text-sm text-gray-700">{{ $t('go.fields.exclude_ddt') }}</span>
            </label>
          </div>

          <div class="flex justify-end gap-2 mt-6">
            <button class="bg-gray-300 px-4 py-2 rounded hover:bg-gray-400" @click="cancelDetailEdit">{{ $t('go.actions.cancel') }}</button>
            <button class="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              @click="applyDetailChanges" v-if="!blockedEdits">{{ $t('go.actions.apply') }}</button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showArticleModal" class="fixed inset-0 flex items-center z-50" :class="{ 'justify-end': embedded, 'justify-center': !embedded }">
      <div class="absolute inset-0 bg-black opacity-50" @click="closeArticleModal"></div>
      <div class="bg-white rounded-lg shadow-xl z-10 max-h-[80vh] overflow-y-auto modal-content" :class="{ 'w-9/12': embedded, 'w-11/12': !embedded }">
        <div class="px-6 py-4 border-b flex items-center justify-between bg-gray-50">
            <h2 class="text-xl font-bold text-gray-800 flex items-center gap-2">
              <v-icon name="co-plus" class="text-blue-600" scale="1.2" />
              {{ $t('go.actions.select') }} {{ kitChangingComponent ? $t('go.modals.select_kit_component') : (articleServiceFilter == 0 ? $t('go.modals.select_goods') : ( articleServiceFilter == 1 ? $t('go.modals.select_service') : $t('go.modals.select_article'))) }}
            </h2>
            <label v-if="$policy('showDisabled').canView" class="flex items-center cursor-pointer">
              <input type="checkbox" v-model="showDisabled" @change="loadArticleTable()" class="sr-only" />
              <div class="relative w-10 h-5 bg-gray-300 rounded-full transition-colors duration-200"
                :class="{ 'bg-green-500': showDisabled }">
                <span
                  class="absolute left-0 top-0 h-5 w-5 bg-white rounded-full shadow transform transition-transform duration-200"
                  :class="{ 'translate-x-5': showDisabled }" />
              </div>
              <span class="ml-3 text-gray-700 text-sm">{{ $t('go.deep_tables.show_disabled') }}</span>
            </label>
        </div>
        <div class="p-6 pt-3">
          <PageServerTable :headers="articleTableHeaders" :items="localArticleTableItems"
            :items-per-page.sync="articleTableipp" :items-per-page-options="articleTableippo"
            :sort-by="localArticleTablesortby" :items-length="localArticleTableCount" :reload-fn="loadArticleTable"
            :item-value="'Item'" density="compact" :page="articleTablepage"
            @click:row="articleCallback" hover
            show-current-page>

            <template #no-data>
              <span class="text-gray-500 text-sm" v-if="!articleTableHeaders.find(x=> x.filtervalue != '' && (x.value == 'Item' || x.value == 'Description')) || !$policy('addFuoriMagazzino').canView">{{ $t('go.deep_tables.no_elements') }}</span>
              <button
                @click="fuoriMagazzino()"
                class="bg-green-400 disabled:bg-green-300 text-white px-4 py-2 rounded hover:bg-green-500 m-2"
                :disabled="blockedEdits || $policy('addFuoriMagazzino').props.disabled"
                v-if="articleTableHeaders.find(x=> x.filtervalue != '' && (x.value == 'Item' || x.value == 'Description')) && $policy('addFuoriMagazzino').canView"
                >
                {{ $t('go.actions.insert_as_out_of_stock') }}
              </button>
            </template>

            <template #item.actions="{ item }">
              <div class="hover:text-blue-500 z-100" @click.stop="getArticleInfo(item.Item)">
                <button>
                  <v-icon name="bi-info-circle" scale="1.2" />
                </button>
              </div>
            </template>

          </PageServerTable>
        </div>
      </div>
    </div>

    <div v-if="showDeepestSelectionTableModal" class="fixed inset-0 flex items-center z-50" :class="{ 'justify-end': embedded, 'justify-center': !embedded }">
      <div class="absolute inset-0 bg-black opacity-50" @click="closeDeepTable"></div>
      <div class="bg-white rounded-lg shadow-xl z-10 max-h-[80vh] overflow-y-auto modal-content" :class="{ 'w-9/12': embedded, 'w-11/12': !embedded }">
        <div class="px-6 py-4 border-b flex items-center justify-between bg-gray-50">
            <h2 class="text-xl font-bold text-gray-800 flex items-center gap-2">
              <v-icon name="md-managesearch" class="text-blue-600" scale="1.2" />
              {{ deepTableTitle }}
            </h2>
            <div class="flex items-center gap-4">
              <label v-if="$policy('showDisabledDeep').canView && showDeepTableDisabled()" class="flex items-center cursor-pointer">
                <input type="checkbox" v-model="showDisabled" @change="loadDeepTable()" class="sr-only" />
                <div class="relative w-10 h-5 bg-gray-300 rounded-full transition-colors duration-200"
                  :class="{ 'bg-green-500': showDisabled }">
                  <span
                    class="absolute left-0 top-0 h-5 w-5 bg-white rounded-full shadow transform transition-transform duration-200"
                    :class="{ 'translate-x-5': showDisabled }" />
                </div>
                <span class="ml-3 text-gray-700 text-sm">{{ $t('go.deep_tables.show_disabled') }}</span>
              </label>
              <label v-if="localHead?.doc_status != 'order'" class="flex items-center cursor-pointer">
                <input type="checkbox" @change="!contactSelection ? showTableContact() : showTableCustomer()" class="sr-only" />
                <div class="relative w-10 h-5 bg-gray-300 rounded-full transition-colors duration-200"
                  :class="{ 'bg-green-500': contactSelection }">
                  <span
                    class="absolute left-0 top-0 h-5 w-5 bg-white rounded-full shadow transform transition-transform duration-200"
                    :class="{ 'translate-x-5': contactSelection }" />
                </div>
                <span class="ml-3 text-gray-700 text-sm">{{ $t('go.deep_tables.use_contacts') }}</span>
              </label>
            </div>
        </div>
        <div class="p-6 pt-3">
          <PageServerTable :headers="localDeepTableHeaders" :items="localDeepTableItems" :items-per-page.sync="localDeepTableipp"
            :items-per-page-options="deepTableippo" :sort-by="localDeepTablesortby" :items-length="localDeepTablecount"
            :reload-fn="deepTableType === 'Offert' ? getOffertList : loadDeepTable" @click:row="handleDeepTableClick" class="deepTable" density="compact" hover
            :page="localDeepTablepage" show-current-page>

          <template #item.doc_date="{ item }">
            <span>
              {{ item.doc_date ? formatDate(item.doc_date) : '-' }}
            </span>
          </template>
          
          </PageServerTable>
        </div>
      </div>

    </div>

    <ShowItemInfo :show="showItemInfo" :item="currentItem" :show-pricelist-info="showPricelistInfo" :open-pricelist="openPricelistDirect" :customer="localHead?.is_contact != 1 ? (localHead?.customer_data?.CustSupp ?? '') : ''" :unit-value-decimals="unitValueDecimals" :quantity-decimals="quantityDecimals" @close="closeItemInfo" @pricelist-selected="onPricelistSelected" />

    <div v-if="showCustomerNotes" class="fixed inset-0 flex items-center justify-center z-50 p-4">
      <div class="absolute inset-0 bg-gray-900/60 backdrop-blur-sm" @click="showCustomerNotes = false"></div>
      <div class="bg-white rounded-2xl shadow-2xl z-10 w-full max-w-[90vw] max-h-[80vh] flex flex-col overflow-hidden">
          <!-- Header -->
          <div class="px-6 py-4 border-b flex items-center justify-between bg-gray-50">
            <h2 class="text-xl font-bold text-gray-800 flex items-center gap-2">
              <v-icon name="hi-annotation" class="text-amber-500" scale="1.2" />
              {{ $t('go.modals.customer_notes_title') }} {{ localHead?.customer_data?.CompanyName }}
            </h2>
          </div>

          <!-- Content -->
          <div class="p-6 overflow-y-auto space-y-6">
              <div v-if="custsuppnote" class="bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r-lg shadow-sm">
                <h3 class="text-sm font-bold uppercase tracking-wider text-amber-800 mb-2 flex items-center gap-2">
                  <v-icon name="bi-info-circle" scale="0.9" />
                  {{ $t('go.modals.internal_notes') }}
                </h3>
                <p class="text-sm text-amber-900 whitespace-pre-wrap leading-relaxed italic">{{ custsuppnote }}</p>
              </div>

              <div v-if="custsuppmdanote" class="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg shadow-sm">
                <h3 class="text-sm font-bold uppercase tracking-wider text-blue-800 mb-2 flex items-center gap-2">
                  <v-icon name="bi-info-circle" scale="0.9" />
                  {{ $t('go.modals.internal_notes_2') }}
                </h3>
                <p class="text-sm text-blue-900 whitespace-pre-wrap leading-relaxed italic">{{ custsuppmdanote }}</p>
              </div>

              <div v-if="custsupprownotes && custsupprownotes.length > 0" class="bg-gray-50 border-l-4 border-gray-400 p-4 rounded-r-lg shadow-sm">
                <h3 class="text-sm font-bold uppercase tracking-wider text-gray-700 mb-2 flex items-center gap-2">
                  <v-icon name="bi-list-ul" scale="0.9" />
                  {{ $t('go.modals.other_notes') }}
                </h3>
                <ul class="list-disc list-inside space-y-2">
                  <li v-for="note in custsupprownotes" :key="note" class="text-sm text-gray-800 italic leading-relaxed">
                    {{ note }}
                  </li>
                </ul>
              </div>

              <div v-if="!custsuppnote && !custsuppmdanote && (!custsupprownotes || custsupprownotes.length === 0)" class="text-center py-8 text-gray-500 italic">
                {{ $t('go.modals.no_customer_notes') }}
              </div>
          </div>
      </div>
    </div>

    <div v-if="showDMSAttachmentsModal" class="fixed inset-0 flex items-center justify-center z-50">
        <div class="absolute inset-0 bg-black opacity-50" @click="showDMSAttachmentsModal = false"></div>
        <div class="bg-white rounded-lg shadow-xl p-6 z-10  overflow-y-auto modal-content">
          <h2 class="text-2xl font-semibold mb-4">{{ $t('go.modals.dms_attachments') }}</h2>
          <div>
            <template v-for="attachment in dmsAttachments" :key="attachment.id">
              <a :href="route('modules.visite-commerciali.allegatoDMS', attachment.id)" target="_blank" class="block bg-yellow-400 disabled:bg-yellow-300 text-white px-4 py-2 rounded hover:bg-yellow-500 mb-2">
              {{ attachment.name }}
              </a>
            </template>
          </div>

        </div>
    </div>

    <div v-if="showSignatureModal"
      class="fixed inset-0 bg-gray-900 bg-opacity-95 flex items-center justify-center z-50">
      <div class="bg-white p-6 rounded-md w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
        <h2 class="text-xl mb-4 text-center">{{ $t('go.actions.customer_signature') }}
        </h2>
        <div class="mb-4">
          <VueSignaturePad ref="signaturePad" width="100%" height="400px" :options="{ penColor: 'black' }"
            class="rounded-md shadow-lg border" />
        </div>
        <div class="flex justify-between">
          <button @click="clearSignature" type="button" class="p93__btn_red flex">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24"
              stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            {{ $t('go.actions.clear_signature') }}
          </button>
          <div class="flex gap-3">
            <button @click="showSignatureModal = false" type="button" class="p93__btn_grey">
              {{ $t('go.actions.cancel') }}
            </button>
            <button @click="openSignatureConfirm" type="button" class="p93__btn_primary flex">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24"
                stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
              </svg>
              {{ $t('go.actions.save_signature') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Popup Gmail-style -->
    <div v-if="showEmailModal" class="fixed bottom-0 right-4 w-full max-w-lg z-[60] shadow-2xl rounded-t-lg overflow-hidden border border-gray-300 bg-white flex flex-col min-h-[500px] max-h-[80vh]">
        <div class="bg-gray-800 text-white px-4 py-2 flex justify-between items-center">
            <span class="font-medium text-sm">{{ $t('go.actions.send_mail') }}</span>
            <div class="flex items-center gap-2">
                <button @click="showEmailModal = false" class="hover:bg-gray-700 p-1 rounded">
                    <v-icon name="co-x" scale="0.8" />
                </button>
            </div>
        </div>
        <div class="flex-1 flex flex-col overflow-hidden">
            <div class="px-4 py-2 border-b flex items-center text-sm">
                <span class="text-gray-500 w-12">A</span>
                <span v-if="emailForm.customer_email">{{ emailForm.customer_email }}</span>
                <select v-else-if="emailForm.linked_emails.length > 0" v-model="emailForm.to" class="border-none focus:ring-0 text-gray-800 mr-2">
                  <option v-for="email in emailForm.linked_emails" :key="email.email" :value="email.email"><span v-if="email.name != '' || email.lastname != ''">{{ email.name }} {{ email.lastname }} ({{ email.email }})</span><span v-else>{{ email.email }}</span></option>
                </select>
                <textarea v-else v-model="emailForm.to" 
                  @input="resizeTextArea($event.target)"  class="flex-1 border-none focus:ring-0 text-gray-800" :placeholder="$t('go.modals.to_placeholder')" ref="emailto"></textarea>
            </div>
            <div class="px-4 py-2 border-b text-sm">
                <div v-if="emailForm.cc_selections.length > 0" class="mb-2 space-y-1">
                    <label v-for="sel in emailForm.cc_selections" :key="sel.email" class="flex items-center gap-2 text-gray-700 cursor-pointer select-none">
                        <input type="checkbox" v-model="sel.checked" class="rounded" />
                        <span>{{ sel.label }}</span>
                    </label>
                </div>
                <div class="flex items-center">
                    <span class="text-gray-500 w-12">Cc</span>
                    <textarea v-model="emailForm.cc"
                        @input="resizeTextArea($event.target)" class="flex-1 border-none focus:ring-0 text-gray-800" :placeholder="$t('go.modals.cc_placeholder')" ref="emailcc"></textarea>
                </div>
            </div>
            <div class="px-4 py-2 border-b flex items-center text-sm">
                <input v-model="emailForm.subject" type="text" class="flex-1 border-none focus:ring-0 text-gray-800 font-medium" :placeholder="$t('go.modals.subject_placeholder')" />
            </div>
            <div class="flex-1 p-4 overflow-y-auto">
                <textarea v-model="emailForm.body" class="w-full h-full border-none focus:ring-0 resize-none text-gray-800 text-sm min-h-[220px]" :placeholder="$t('go.modals.body_placeholder')"></textarea>
            </div>
        </div>
        <div class="p-4 border-t bg-white flex items-center justify-between">
            <div class="flex items-center gap-4">
                <button @click="sendEmail" class="bg-blue-600 text-white px-6 py-2 rounded-full font-medium hover:bg-blue-700 shadow-md">
                    {{ $t('go.actions.send') }}
                </button>
            </div>
        </div>
    </div>

  </div>

  <div v-if="showKitModal" class="fixed inset-0 z-20 flex items-center justify-center bg-black bg-opacity-50">
    <div class="bg-white rounded-lg shadow-xl w-full max-w-5xl mx-4 flex flex-col max-h-[90vh]">
      <div class="px-6 py-4 border-b flex items-center justify-between bg-gray-50 rounded-t-lg">
        <h2 class="text-lg font-bold text-gray-800">Configurazione Kit</h2>
        <button @click="showKitModal = false" class="text-gray-400 hover:text-gray-600">
          <v-icon name="co-x" scale="1.2" />
        </button>
      </div>

      <div class="px-6 py-4 border-b">
        <div class="flex flex-wrap items-center gap-x-6 gap-y-2">
          <div class="flex items-center gap-3">
            <label class="text-sm font-medium text-gray-700 whitespace-nowrap">Quantità kit:</label>
            <input
              type="number"
              v-model.number="kitQty"
              min="1"
              class="w-24 border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div v-if="kitPriceListOptions.length" class="flex items-center gap-3">
            <label class="text-sm font-medium text-gray-700 whitespace-nowrap">Listino kit:</label>
            <select
              v-model="kitPriceList"
              class="border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">-- Nessuno --</option>
              <option v-for="pl in kitPriceListOptions" :key="pl.PriceList" :value="pl.PriceList">
                {{ pl.PriceList }} - {{ pl.Description }}
              </option>
            </select>
            <span v-if="kitPriceListLoading" class="text-xs text-gray-500 italic">Aggiornamento prezzi...</span>
          </div>
        </div>
        <div class="flex flex-wrap items-start gap-x-6 gap-y-2 mt-3">
          <div class="flex items-center gap-3">
            <label class="text-sm font-medium text-gray-700 whitespace-nowrap">Data prevista consegna:</label>
            <VueDatePicker v-model="kitExpectedDelivery" locale="it" format="dd/MM/yyyy" :placeholder="$t('ui.choose_date')"
              :enable-time-picker="false" class="border rounded-md" :teleport="true" auto-apply text-input style="width:180px" />
          </div>
          <div class="flex items-start gap-3 flex-1">
            <label class="text-sm font-medium text-gray-700 whitespace-nowrap mt-1">Nota descrittiva:</label>
            <textarea v-model="kitNota" rows="2"
              class="flex-1 border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              placeholder="Nota applicata alla riga descrittiva del kit"></textarea>
          </div>
        </div>
      </div>

      <div class="flex-1 overflow-y-auto px-6 py-4">
        <p class="text-sm text-gray-500 mb-3">
          Seleziona i componenti da includere nel kit. I componenti obbligatori sono pre-selezionati e non possono essere rimossi.
        </p>
        <v-treeview
          :items="kitTreeItems"
          v-model:selected="kitSelectedComponents"
          selectable
          select-strategy="leaf"
          open-all
          density="compact"
          item-value="id"
          item-title="title"
        >
          <template v-slot:prepend="{ item }">
            <v-icon
              v-if="item.isRoot"
              name="md-inventory-round"
              class="text-blue-500"
              scale="1.1"
            />
            <v-icon
              v-else-if="item.mandatory"
              name="fa-lock"
              class="text-red-500"
              scale="1.0"
            />
            <v-icon
              v-else-if="item.notes && item.notes.length"
              name="hi-annotation"
              class="text-amber-500"
              scale="1.1"
            />
            <v-icon
              v-else
              name="md-inventory-round"
              class="text-gray-400"
              scale="1.0"
            />
          </template>

          <template v-slot:title="{ item }">
            <div class="flex flex-col w-full py-0.5">
              <div class="flex items-center gap-2 text-sm">
                <span>{{ item.title }}</span>
              </div>
              <ul v-if="item.notes && item.notes.length" class="mt-1 space-y-0.5">
                <li v-for="(note, i) in item.notes" :key="i" class="text-xs text-amber-700">{{ note }}</li>
              </ul>
            </div>
          </template>

          <template v-slot:append="{ item }">
            <div v-if="!item.isRoot" class="flex items-center gap-2 mr-2">
              <template v-if="kitSelectedComponents.includes(item.id)">
                <label class="text-xs text-gray-500 whitespace-nowrap">Qtà:</label>
                <input
                  type="number"
                  :value="kitComponentQtys[item.id]"
                  @input.stop="kitComponentQtys[item.id] = parseFloat($event.target.value) || 0"
                  @click.stop
                  min="0"
                  step="0.0001"
                  class="w-20 border border-gray-300 rounded px-2 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                <span v-if="item.component && (kitComponentPriceOverrides[item.id]?.unit_value || parseFloat(item.component.BasePrice))" class="text-xs text-gray-600 w-24 text-right whitespace-nowrap">
                  € {{ (kitComponentPriceOverrides[item.id]?.unit_value ?? parseFloat(item.component.BasePrice) ?? 0)  }} {{ item.component.DiscountFormula ? `SC: ${item.component.DiscountFormula}` : '' }}
                </span>
              </template>
              <button
                v-if="!item.mandatory"
                @click.stop="changeKitComponent(item.id)"
                class="ml-1 flex items-center gap-1 px-2 py-0.5 text-xs text-blue-600 border border-blue-300 rounded hover:bg-blue-50"
                title="Cambia componente"
              >
                <v-icon name="fa-edit" scale="0.85" />
                Cambia
              </button>
            </div>
          </template>
        </v-treeview>
      </div>

      <div class="px-6 py-3 border-t bg-gray-50">
        <div v-if="kitTotalPrice > 0" class="text-sm font-medium text-gray-700 text-right mb-3">
          Totale componenti: <span class="text-blue-700">€ {{ kitTotalPrice.toFixed(2) }}</span>
        </div>
        <div class="flex justify-end gap-3">
          <button
            @click="showKitModal = false"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
          >
            Annulla
          </button>
          <button
            @click="confirmKitSelection"
            class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700"
          >
            Conferma
          </button>
        </div>
      </div>
    </div>
  </div>

  <ToastNotification :show="toast.show" :message="toast.message" :type="toast.type"
    @update:show="toast.show = $event" />
</template>



<script setup>
import { Link, router, useForm, usePage } from '@inertiajs/vue3';
import BreadCrumbs from '@/Components/BreadCrumbs.vue';
import Pagination from '@/Components/Pagination.vue';
import PageNormalTable from '@/Components/PageNormalTable.vue';
import PageServerTable from '@/Components/PageServerTable.vue';
import ToastNotification from '@/Components/ToastNotification.vue';
import { VueSignaturePad } from 'vue-signature-pad';
import ConfirmationModal from '@/Components/ConfirmationModal.vue';
import DangerButton from '@/Components/DangerButton.vue';
import SecondaryButton from '@/Components/SecondaryButton.vue';
import Loader from '@/Components/Loader.vue';
import ShowItemInfo from '@/Components/ShowItemInfo.vue';
import axios from 'axios';
</script>

<script>
import currencyMixin from '@/mixins/currencyMixin';

export default {
  mixins: [currencyMixin],
  props: ['head', 'details', 'owner', 'last_editor', 'salespeople', 'pricelists', 'deepTableItems', 
  'deepTablecount', 'articleTableItems', 'articleTableCount', 'error', 'embedded', 
  'allowPriceRowZero', 'handleSignature', 'showDMSContent', 'carriers', 
  'blockChangesAfterMagoSent', 'emailSend', 'forceAdminCorrection', 'useThousandSeparator','personalizzazioniImel',
  'handleWeee', 'handleKits', 'packageMode', 'showNotesOnNotesRows', 'showPricelistInfo', 'showRowPricelist', 'noteRowsExcludeFromInvoiceDdt', 'enableOfferMagoSend',
  'enableDocumentLocking', 'documentLock', 'enableContractCreation', 'unitValueDecimals', 'quantityDecimals',
  'allowDraftPrint', 'explicitReview', 'enableOrderPeopleSelection', 'availablePeople'],
  emits: ['go-back-to-home', 'reload-mails'],
  components: {
    Link,
    BreadCrumbs,
    Pagination,
    PageNormalTable,
    PageServerTable,
    ToastNotification,
    Loader,
    ShowItemInfo,
    VueSignaturePad
  },
  data() {
    return {
      search: this.filters?.search || "",
      isLoading: false,
      loadingText: '',
      toast: {
        show: false,
        message: '',
        type: 'success'
      },
      branches: [],
      peopleOptions: this.availablePeople || [],
      localDetails: [],
      detailHeaders: [],
      selectedTab: 'main',
      showOrderDetails: true,
      //deletions: [],
      showFilters: false,
      handleDeepTableClick: undefined,
      articleServiceFilter: false,
      showArticleModal: false,
      editDetailModal: false,
      detailData: undefined,
      packageQty: 0,
      isNewDetail: false, // Flag to track if we're adding a new detail or editing existing
      calculateTimeout: null,
      isCalculatingRowTotals: false,
      showDeepestSelectionTableModal: false,
      articleTableHeaders: [
        { title: this.$t('go.fields.item_article'), value: 'Item', key: 'Item', filterable: true, filtervalue: "" },
        { title: this.$t('go.fields.description'), value: 'Description', key: 'Description', filterable: true, filtervalue: "" },
        { title: this.$t('go.fields.homogeneous_ctg'), value: 'HomogeneousCtg', key: 'HomogeneousCtg', callback: this.showTableHomogeneousCtg, filterable: true, filtervalue: "" },
        { title: this.$t('go.fields.commodity_ctg'), value: 'CommodityCtg', key: 'CommodityCtg', callback: this.showTableCommodityCtg, filterable: true, filtervalue: "" },
        { title: this.$t('go.fields.product_ctg'), value: 'ProductCtg', key: 'ProductCtg', callback: this.showTableProductCtg, filterable: true, filtervalue: "" },
        { title: this.$t('go.fields.item_type'), value: 'ItemType', key: 'ItemType', callback: this.showTableItemType, filterable: true, filtervalue: "" },
        { title: this.$t('go.fields.producer'), value: 'Producer', key: 'Producer', callback: this.showTableProducer, filterable: true, filtervalue: "" },
        { title: this.$t('go.fields.info'), value: 'actions', key: 'actions', "sortable": false, filterable: false, filtervalue: "" },
      ],
      articleTableipp: 10,
      articleTablesortby: [{ "key": "Item", "order": "asc" }],
      articleTableippo: [
        { "value": 10, "title": '10' },
        { "value": 25, "title": '25' },
        { "value": 50, "title": '50' },
        { "value": 100, "title": '100' }
      ],
      articleTablepage: 1,
      deepTableType: '',
      deepTableTitle: '',
      deepTableDefine: false,
      deepTableHeaders: [],
      deepTableipp: 10,
      deepTablesortby: [],
      deepTableippo: [
        { "value": 10, "title": '10' },
        { "value": 25, "title": '25' },
        { "value": 50, "title": '50' },
        { "value": 100, "title": '100' }
      ],
      deepTablepage: 1,
      orderTotals: {
        goods_total: 0,
        services_total: 0,
        subtotal: 0,
        discount_amount: 0,
        taxable_amount: 0,
        tax_amount: 0,
        total_amount: 0,
        total_freesamples_amount: 0,
        total_weee_amount: 0,
        vat_breakdown: []
      },
      availabilities: [],
      itemLocations: [],
      calculateOrderTimeout: null,
      hasUnsavedChanges: false,
      showItemInfo: false,
      currentItem: '',
      openPricelistDirect: false,
      localDeepTableHeaders: [],
      localDeepTableItems: [],
      localDeepTablecount: 0,
      localDeepTableipp: 10,
      localDeepTablesortby: [],
      localDeepTablepage: 1,
      localArticleTableItems: [],
      localArticleTableCount: 0,
      localArticleTablesortby: [],
      localHead: null,
      showDisabled: false,
      showCustomerNotes: false,
      custsuppnote: '',
      custsuppmdanote: '',
      custsupprownotes: [],
      dmsAttachments: [],
      showDMSAttachmentsModal: false,
      showSignatureModal: false,
      showCreateContractModal: false,
      selectedContractRowIds: [],
      selectAllContractRows: false,
      showAgentConfirmModal: false,
      showDeleteModal: false,
      showDeleteModalRow: false,
      showSignatureConfirm: false,
      available_uoms: [],
      showVatBreakdown: false,
      currentItemNotes: [],
      warnQty: 0,
      contactSelection: false,
      showWarnQtyModal: false,
      showEmailModal: false,
      showLockModal: false,
      showKickedModal: false,
      heartbeatInterval: null,
      forcedReadOnly: false,
      emailForm: {
        to: '',
        cc: '',
        cc_selections: [],
        subject: '',
        body: '',
        customer_email: '',
        linked_emails: []
      },
      showKitModal: false,
      kitComponents: [],
      kitSelectedComponents: [],
      kitComponentQtys: {},
      kitNotes: {},
      kitPendingDetail: null,
      kitQty: 1,
      kitChangingComponent: null,
      kitPriceList: '',
      kitComponentPriceOverrides: {},
      kitPriceListNotes: {},
      kitPriceListLoading: false,
      kitNota: '',
      kitExpectedDelivery: null,
      kitComponentWeee: {},
    };
  },
  computed: {
    tabs() {
      return [
        { name: this.$t('go.tabs.main'), value: 'main' },
        { name: this.$t('go.tabs.document_notes'), value: 'documentnotes' },
        { name: this.$t('go.tabs.shipping'), value: 'shipping' },
        { name: this.$t('go.tabs.charges'), value: 'charges' },
      ];
    },
    todayDate() {
      return new Date();
    },
    blockedEdits(){
      return this.forcedReadOnly || (this.localHead && ((this.localHead.blocked) || (this.localHead.new_order_number) || (this.blockChangesAfterMagoSent && this.localHead.mago_doc_order_no) || (this.forceAdminCorrection && ((this.localHead.approval == 1 || this.localHead.correctable == true) && !this.$page.props.isAdminOrSuperAdmin))));
    },
    linkedPeopleSelection() {
      return this.localHead.linked_people ? this.localHead.linked_people.split(',').filter(Boolean) : [];
    },
    contractEligibleDetails() {
      return (this.localDetails || []).filter(d =>
        d.cancelled != '1' &&
        d.line_type != this.MagoEnums.Line_Type.Notes &&
        d.line_type != this.MagoEnums.Line_Type.Descriptive
      );
    },
    contractSelectableDetails() {
      return this.contractEligibleDetails.filter(d => !d.contract_docno);
    },
    vatBreakdownTotals() {
      if (!this.orderTotals.vat_breakdown || this.orderTotals.vat_breakdown.length === 0) {
        return { taxable: 0, tax: 0, total: 0 };
      }
      return this.orderTotals.vat_breakdown.reduce((acc, item) => {
        acc.taxable += parseFloat(item.taxable) || 0;
        acc.tax += parseFloat(item.tax) || 0;
        acc.total += parseFloat(item.total) || 0;
        return acc;
      }, { taxable: 0, tax: 0, total: 0 });
    },
    navigation() {
      if (!this.localHead || !this.localHead.doc_status) {
        return [
          { name: 'Dashboard', href: route('dashboard') },
          { name: this.$t('go.breadcrumb.gestione_ordini'), href: route('modules.home', 'gestione-ordini') }
        ];
      }
      
      const nav = this.localHead.doc_status !== 'order' ?
        [
          { name: 'Dashboard', href: route('dashboard') },
          { name: this.$t('go.breadcrumb.gestione_ordini'), href: route('modules.home', 'gestione-ordini') },
          { name: this.$t('go.breadcrumb.lista_proposte_offerte'), href: route('modules.ordini.lista-offerte') }
        ]
        :
        [
          { name: 'Dashboard', href: route('dashboard') },
          { name: this.$t('go.breadcrumb.gestione_ordini'), href: route('modules.home', 'gestione-ordini') },
          { name: this.$t('go.breadcrumb.lista_ordini'), href: route('modules.ordini.lista-ordini') }
        ];
      return nav;
    },
    kitTotalPrice() {
      return this.kitComponents
        .filter(c => this.kitSelectedComponents.includes(c.Component))
        .reduce((sum, c) => {
          const qty = parseFloat(this.kitComponentQtys[c.Component]) || 0;
          const override = this.kitComponentPriceOverrides[c.Component];
          const price = override ? override.unit_value : (parseFloat(c.BasePrice) || 0);
          return sum + qty * price;
        }, 0);
    },
    kitPriceListOptions() {
      return (this.pricelists || []).filter(pl => pl.PkListinoKit == 1);
    },
    kitTreeItems() {
      if (!this.kitPendingDetail || !this.kitComponents.length) return [];
      return [{
        id: '__root__',
        title: `${this.kitPendingDetail.item} - ${this.kitPendingDetail.description}`,
        isRoot: true,
        children: this.kitComponents.map(c => ({
          id: c.Component,
          title: `${c.Component} - ${c.Description || ''}`,
          mandatory: c.PkObbligatorio == 1,
          isRoot: false,
          component: c,
          notes: [
            ...(this.kitNotes[c.Component] || []),
            ...(this.kitPriceListNotes[c.Component] || []),
          ],
        }))
      }];
    }
  },
  watch: {
    // Watchers per ricalcolare automaticamente i totali quando cambiano i valori nella modale
    /*'detailData.unit_value': function (newVal) {
      if (this.detailData && newVal) {
        this.calculateRowTotalsFromServer();
      }
    },
    'detailData.discount_formula': function (newVal) {
      if (this.detailData) {
        this.calculateRowTotalsFromServer();
      }
    },
    'detailData.tax_code': function (newVal) {
      if (this.detailData) {
        this.calculateRowTotalsFromServer();
      }
    },*/
    // Watchers per i totali dell'ordine
    'kitSelectedComponents': function(newVal) {
      const mandatoryIds = this.kitComponents
        .filter(c => c.PkObbligatorio == 1)
        .map(c => c.Component);
      const missing = mandatoryIds.filter(id => !newVal.includes(id));
      if (missing.length > 0) {
        this.kitSelectedComponents = [...newVal, ...missing];
      }
    },
    'kitPriceList': function(newVal) {
      this.fetchKitComponentPrices(newVal);
    },
    'kitQty': function(newVal) {
      const qty = parseFloat(newVal) || 1;
      this.kitComponents.forEach(c => {
        const base = parseFloat(c._kitIssuedQty ?? c.IssuedQty) || 1;
        this.kitComponentQtys[c.Component] = parseFloat((base * qty).toFixed(4));
      });
    },
    'localHead.discount_formula': function (newVal) {
      this.calculateOrderTotalsFromServer();
    },
    'localHead.allowances': function (newVal) {
      this.calculateOrderTotalsFromServer();
    },
    'localHead.payment_term': function (newVal) {
      this.calculateOrderTotalsFromServer();
    },
    'localHead.transport_price': function (newVal) {
      this.calculateOrderTotalsFromServer();
    },
    'localHead.other_price': function (newVal) {
      this.calculateOrderTotalsFromServer();
    },
    'localHead.collection_charges_price': function (newVal) {
      this.calculateOrderTotalsFromServer();
    },
    'localHead.carrier1': function (newVal) {
      this.calculateOrderTotalsFromServer();
    },
    'error': function (newVal) {
      if (newVal) {
        // Mostra un toast di errore se c'è un messaggio di errore
        this.showToast(newVal, 'error');
      }
    },
    'deepTableItems': function (newVal) {
      if (newVal && !this.embedded) {
        this.localDeepTableItems = [...newVal];
      }
    },
    'deepTablecount': function (newVal) {
      if (newVal !== undefined && !this.embedded) {
        this.localDeepTablecount = newVal;
      }
    },
    'articleTableItems': function (newVal) {
      if (newVal && !this.embedded) {
        this.localArticleTableItems = [...newVal];
      }
    },
    'articleTableCount': function (newVal) {
      if (newVal !== undefined && !this.embedded) {
        this.localArticleTableCount = newVal;
      }
    },
    'articleTablesortby': function (newVal) {
      if (newVal && !this.embedded) {
        this.localArticleTablesortby = [...newVal];
      }
    },
    'head': {
      handler: function (newVal) {
        if (newVal) {
          this.localHead = JSON.parse(JSON.stringify(newVal));
        }
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    getRowAvailabilityStatus(item) {
      // Se non è merce non considerarle
      if (item.line_type != this.MagoEnums.Line_Type.Goods) return null;
      
      const availability = this.availabilities.find(a => a.Item === item.item);
      if (!availability) return 'none';

      let cumulativeQty = 0;
      const index = this.localDetails.indexOf(item);
      
      // Fai la sommatoria fino a quel punto considerando solo le righe di tipo merce con lo stesso articolo
      for (let i = 0; i <= index; i++) {
        const row = this.localDetails[i];
        if (row.item === item.item && row.line_type == this.MagoEnums.Line_Type.Goods) {
          cumulativeQty += parseFloat(row.qty) || 0;
        }
      }

      if (cumulativeQty <= (parseFloat(availability.GreenQty) || 0)) return 'green';
      if (cumulativeQty <= (parseFloat(availability.YellowQty) || 0)) return 'yellow';
      return 'red';
    },
    getItemLocation(item){
        if (item.line_type != this.MagoEnums.Line_Type.Goods) return '-';

        let location = this.itemLocations.find(x => x.Item == item.item);

        if (location){
          return location.Location;
        }
        else return '-';

    },
    openEmailModal() {
      if (this.localHead.is_contact != 1 && !this.localHead.customer_data?.CustSupp) {
        this.showToast(this.$t('go.messages.select_customer_before_mail'), 'error');
        return;
      }
      else if (this.localHead.is_contact == 1 && !this.localHead.customer_data?.Contact) {
        this.showToast(this.$t('go.messages.select_contact_before_mail'), 'error');
        return;
      }

      this.isLoading = true;
      this.loadingText = this.$t('go.messages.fetching_emails');
      
      axios.post(route('modules.ordini.customerEmails'), {
        reference: this.localHead.is_contact == 1 ? this.localHead.customer_data?.Contact : this.localHead.customer_data?.CustSupp, 
        isContact: this.localHead.is_contact,
        salesperson: this.localHead.sales_person
      }).then(response => {
        const data = response.data;
        if (data.success) {
          this.emailForm.customer_email = data.mainEmail;
          this.emailForm.linked_emails = data.linkedEmails || [];

          if (this.emailForm.customer_email != '') {
            this.emailForm.to = this.emailForm.customer_email;
          } else if (this.emailForm.linked_emails.length > 0) {
            this.emailForm.to = this.emailForm.linked_emails[0].email;
          }

          this.emailForm.cc_selections = [];
          const agentEmail = data.salesPersonEmail;
          if (agentEmail) {
            const fullName = [agentEmail.name, agentEmail.lastname].filter(Boolean).join(' ');
            this.emailForm.cc_selections.push({
              email: agentEmail.email,
              label: fullName ? `${fullName} (${agentEmail.email})` : agentEmail.email,
              checked: true
            });
          }

          data.linkedEmails.forEach(item => {
            const fullName = [item.name, item.lastname].filter(Boolean).join(' ');
            this.emailForm.cc_selections.push({
              email: item.email,
              label: fullName ? `${fullName} (${item.email})` : item.email,
              checked: false
            });
          });
          
          const docType = this.$t(`go.doc_status.${this.localHead.doc_status}`);
          this.emailForm.subject = `${docType} n. ${this.localHead.doc_no} - ${this.localHead.customer_data.CompanyName}`;
          this.emailForm.body = this.$t('go.messages.email_body_template', {'companyname': this.localHead.customer_data.CompanyName});
          
          this.showEmailModal = true;
          this.$nextTick(() => {
            this.resizeTextArea(this.$refs.emailto);
            this.resizeTextArea(this.$refs.emailcc);
          });
        } else {
          this.showToast(data.message || this.$t('go.messages.error_loading_emails'), 'error');
        }
      }).catch(error => {
        this.showToast(this.$t('go.messages.error_loading_emails'), 'error');
      }).finally(() => {
        this.isLoading = false;
      });
    },
    sendEmail() {
      if (!this.emailForm.to) {
        this.showToast(this.$t('go.messages.email_recipient_required'), 'error');
        return;
      }

      this.isLoading = true;
      this.loadingText = this.$t('go.messages.generating_pdf');

      const checkedEmails = this.emailForm.cc_selections.filter(s => s.checked).map(s => s.email);
      const manualEmails = this.emailForm.cc.split(';').map(e => e.trim()).filter(e => e);
      const allCc = [...new Set([...checkedEmails, ...manualEmails])].join(';');

      axios.post(route('modules.ordini.inviaEmail'), {
        id: this.localHead.id,
        to: this.emailForm.to,
        cc: allCc,
        subject: this.emailForm.subject,
        body: this.emailForm.body
      }).then(response => {
        if (response.data.success) {
          this.showToast(response.data.message, 'success');
          this.showEmailModal = false;
        } else {
          this.showToast(response.data.message, 'error');
        }
      }).catch(error => {
        this.showToast(this.$t('go.messages.error_sending_email'), 'error');
      }).finally(() => {
        this.isLoading = false;
      });
    },
    showToast(message, type = 'success') {
      this.toast.message = message;
      this.toast.type = type;
      this.toast.show = true;
    },
    toggleVatBreakdown() {
      this.showVatBreakdown = !this.showVatBreakdown;
      
      if (this.showVatBreakdown) {
        this.$nextTick(() => {
          if (this.$refs.vatBreakdownSection) {
            this.$refs.vatBreakdownSection.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'nearest' 
            });
          }
        });
      }
    },
    getKitGroup(item) {
      if (!this.isKitParent(item)) return [item];
      const children = this.localDetails.filter(d => {
        if (item._kitGroupId) return d._kitParentGroupId === item._kitGroupId;
        return d.kit_parent_id && item.id && d.kit_parent_id == item.id && d.id != item.id;
      });
      return [item, ...children].sort((a, b) => a.position - b.position);
    },
    getKitParentOf(child) {
      if (child._kitParentGroupId) return this.localDetails.find(d => d._kitGroupId === child._kitParentGroupId) || null;
      if (child.kit_parent_id) return this.localDetails.find(d => d.id == child.kit_parent_id && d.id != child.id) || null;
      return null;
    },
    getAdjacentBlock(row) {
      if (this.isKitChild(row)) {
        const parent = this.getKitParentOf(row);
        return parent ? this.getKitGroup(parent) : [row];
      }
      if (this.isKitParent(row)) return this.getKitGroup(row);
      return [row];
    },
    isFirstItem(item) {
      if (this.localDetails.length === 0) return true;
      const group = this.isKitParent(item) ? this.getKitGroup(item) : [item];
      return Math.min(...group.map(r => r.position)) <= 1;
    },
    isLastItem(item) {
      if (this.localDetails.length === 0) return true;
      const group = this.isKitParent(item) ? this.getKitGroup(item) : [item];
      return Math.max(...group.map(r => r.position)) >= this.localDetails.length;
    },
    moveItemUp(item) {
      if (this.isKitChild(item)) return;

      const group = this.getKitGroup(item);
      const groupSorted = [...group].sort((a, b) => a.position - b.position);
      const groupMinPos = groupSorted[0].position;
      if (groupMinPos <= 1) return;

      const sorted = [...this.localDetails].sort((a, b) => a.position - b.position);
      const firstGroupIdx = sorted.indexOf(groupSorted[0]);
      if (firstGroupIdx <= 0) return;

      const blockAbove = this.getAdjacentBlock(sorted[firstGroupIdx - 1]);
      const blockAboveSorted = [...blockAbove].sort((a, b) => a.position - b.position);

      const allPositions = [...blockAboveSorted, ...groupSorted].map(r => r.position).sort((a, b) => a - b);
      groupSorted.forEach((row, i) => { row.position = allPositions[i]; });
      blockAboveSorted.forEach((row, i) => { row.position = allPositions[groupSorted.length + i]; });

      this.hasUnsavedChanges = true;
      this.fixPositions();
      this.showToast(this.$t('go.messages.positions_modified'), 'success');
    },
    moveItemDown(item) {
      if (this.isKitChild(item)) return;

      const group = this.getKitGroup(item);
      const groupSorted = [...group].sort((a, b) => a.position - b.position);
      const groupMaxPos = groupSorted[groupSorted.length - 1].position;
      if (groupMaxPos >= this.localDetails.length) return;

      const sorted = [...this.localDetails].sort((a, b) => a.position - b.position);
      const lastGroupIdx = sorted.indexOf(groupSorted[groupSorted.length - 1]);
      if (lastGroupIdx >= sorted.length - 1) return;

      const blockBelow = this.getAdjacentBlock(sorted[lastGroupIdx + 1]);
      const blockBelowSorted = [...blockBelow].sort((a, b) => a.position - b.position);

      const allPositions = [...groupSorted, ...blockBelowSorted].map(r => r.position).sort((a, b) => a - b);
      blockBelowSorted.forEach((row, i) => { row.position = allPositions[i]; });
      groupSorted.forEach((row, i) => { row.position = allPositions[blockBelowSorted.length + i]; });

      this.hasUnsavedChanges = true;
      this.fixPositions();
      this.showToast(this.$t('go.messages.positions_modified'), 'success');
    },
    swapPositions(index1, index2) {
      if (index1 !== -1 && index2 !== -1) {
        const tempPosition = this.localDetails[index1].position;
        this.localDetails[index1].position = this.localDetails[index2].position;
        this.localDetails[index2].position = tempPosition;
        this.hasUnsavedChanges = true;
        this.fixPositions();
        this.showToast(this.$t('go.messages.positions_modified'), 'success');
      }
    },
    articleCallback(event, row) {
      if (this.kitChangingComponent) {
        this.replaceKitComponent(row.item);
        return;
      }

      // Controlla se l'articolo è già presente
      /*const existingItem = this.localDetails.find(detail => detail.item === row.item.Item);
      if (existingItem) {
        this.showToast(`L'articolo "${row.Description}" è già presente nell'ordine`, 'error');
        return;
      }*/

      row.item.line_type = this.articleServiceFilter == 0 ? this.MagoEnums.Line_Type.Goods : this.articleServiceFilter == 1 ? this.MagoEnums.Line_Type.Service : this.MagoEnums.Line_Type.Descriptive;

      axios.post(route('modules.ordini.articleDetails'), { head: this.localHead, details: this.localDetails, row: row.item })
        .then(response => {
          const details = response.data;
          const newDetail = {
            id: null, // Le nuove righe non hanno ID fino al salvataggio
            item: row.item.Item,
            description: row.item.Description,
            line_type: details.line_type, // Servizio o Merce
            sale_type: this.MagoEnums.Sale_Type.Normal, // Normale
            base_uom: details.base_uom || 'PZ',
            actual_uom: details.actual_uom || 'PZ',
            previous_uom: details.actual_uom || 'PZ',
            qty: 1,
            unit_value: details.unit_value || 0,
            discount_formula: details.discount_formula || '',
            discount1: details.discount1 || '',
            discount2: details.discount2 || '',
            net_price: 0,
            tax_code: details.tax_code || '',
            tax_percentage: details.tax_percentage || 0,
            tax_nocharges: details.tax_nocharges || false,
            taxable_amount: 0,
            tax_amount: 0,
            total_amount: 0,
            position: this.localDetails.length + 1, // Prossima posizione disponibile
            price_list: details.price_list || '',
            //additional_description: '',
            salesperson_comm: details.salesperson_comm || 0,
            nota: '',
            weee_amount: 0,
            weee_tax_code: details.weee_tax_code || '22',
            weee_tax_percentage: details.weee_tax_percentage || 22,
            weee_uom_amount: details.weee_uom_amount || 0,
            weee_ctg: details.weee_ctg || '',
            custom: false,
          };
          
          this.showArticleModal = false;

          if (details.is_kit && this.handleKits) {
            this.loadKitData(row.item.Item, newDetail);
            return;
          }

          // Open modal for new detail instead of adding directly
          this.editDetail(newDetail, true);
        }).catch(error => {
          this.showToast(error.response.data.message || 'Errore nel recupero dei dettagli dell\'articolo', 'error');
        });
    },
    loadKitData(item, pendingDetail) {
      this.isLoading = true;
      this.loadingText = 'Caricamento componenti kit...';
      axios.post(route('modules.ordini.getKitData'), { item })
        .then(response => {
          if (response.data.success) {
            this.kitComponents = response.data.components;
            this.kitPendingDetail = pendingDetail;
            this.kitQty = 1;
            this.kitSelectedComponents = response.data.components
              .filter(c => c.PkObbligatorio == 1)
              .map(c => c.Component);
            const qtys = {};
            response.data.components.forEach(c => {
              qtys[c.Component] = parseFloat(c.IssuedQty) || 1;
            });
            this.kitComponentQtys = qtys;
            this.kitNotes = response.data.notes || {};
            this.kitComponentWeee = response.data.weee || {};
            this.kitNota = '';
            this.kitExpectedDelivery = null;
            this.showKitModal = true;
          } else {
            this.showToast(response.data.message || 'Errore nel caricamento del kit', 'error');
            this.editDetail(pendingDetail, true);
          }
        })
        .catch(() => {
          this.editDetail(pendingDetail, true);
        })
        .finally(() => {
          this.isLoading = false;
          this.loadingText = '';
        });
    },
    changeKitComponent(componentId) {
      this.kitChangingComponent = componentId;
      this.articleServiceFilter = 0;
      this.loadArticleTable(undefined, undefined, undefined);
    },
    replaceKitComponent(newItem) {
      const oldId = this.kitChangingComponent;
      const oldIndex = this.kitComponents.findIndex(c => c.Component === oldId);
      if (oldIndex === -1) {
        this.kitChangingComponent = null;
        this.showArticleModal = false;
        this.showKitModal = true;
        return;
      }

      const old = this.kitComponents[oldIndex];
      const oldQtyAbsolute = this.kitComponentQtys[oldId] ?? (parseFloat(old._kitIssuedQty ?? old.IssuedQty) || 1) * this.kitQty;
      const ratio = this.kitQty > 0 ? oldQtyAbsolute / this.kitQty : (parseFloat(old._kitIssuedQty ?? old.IssuedQty) || 1);

      this.isLoading = true;
      this.loadingText = 'Caricamento dati componente...';
      this.showArticleModal = false;

      axios.post(route('modules.ordini.getKitSingleComponentData'), { item: newItem.Item, head: this.localHead })
        .then(response => {
          const data = response.data;
          const newComponent = {
            Component: newItem.Item,
            Description: data.success ? data.component.Description : (newItem.Description || newItem.Item),
            BaseUOM: data.success ? data.component.BaseUOM : (newItem.BaseUOM || 'NR'),
            IssuedQty: old.IssuedQty,
            _kitIssuedQty: ratio,
            Line: old.Line,
            PkObbligatorio: 0,
            BasePrice: data.success ? data.component.BasePrice : 0,
            TaxCode: data.success ? data.component.TaxCode : (newItem.TaxCode || ''),
            Discount1: data.success ? data.component.Discount1 : '',
            Discount2: data.success ? data.component.Discount2 : '',
            DiscountFormula: data.success ? data.component.DiscountFormula : '',
          };

          const newComponents = [...this.kitComponents];
          newComponents.splice(oldIndex, 1, newComponent);
          this.kitComponents = newComponents;

          delete this.kitComponentQtys[oldId];
          this.kitComponentQtys[newItem.Item] = oldQtyAbsolute;

          const selIdx = this.kitSelectedComponents.indexOf(oldId);
          if (selIdx !== -1) {
            const newSel = [...this.kitSelectedComponents];
            newSel.splice(selIdx, 1, newItem.Item);
            this.kitSelectedComponents = newSel;
          }

          const newNotes = { ...this.kitNotes };
          delete newNotes[oldId];
          newNotes[newItem.Item] = data.success ? (data.notes || []) : [];
          this.kitNotes = newNotes;

          const newWeee = { ...this.kitComponentWeee };
          delete newWeee[oldId];
          if (data.success && data.weee) newWeee[newItem.Item] = data.weee;
          this.kitComponentWeee = newWeee;
        })
        .catch(() => {
          this.showToast('Errore nel recupero dei dati del componente', 'error');
        })
        .finally(() => {
          this.kitChangingComponent = null;
          this.isLoading = false;
          this.loadingText = '';
        });
    },
    async fetchKitComponentPrices(priceList) {
      this.kitComponentPriceOverrides = {};
      this.kitPriceListNotes = {};
      if (!priceList) return;

      this.kitPriceListLoading = true;
      const items = this.kitComponents.map(c => c.Component);

      try {
        const response = await axios.post(route('modules.ordini.getKitComponentPrices'), {
          pricelist: priceList,
          items,
          head: this.localHead,
        });

        if (response.data.success) {
          const overrides = {};
          const plNotes = {};

          Object.entries(response.data.prices).forEach(([itemCode, priceData]) => {
            const comp = this.kitComponents.find(c => c.Component === itemCode);
            const basePrice = comp ? (parseFloat(comp.BasePrice) || 0) : 0;

            if (priceData.found && priceData.unit_value > 0) {
              overrides[itemCode] = {
                unit_value: priceData.unit_value,
                price_list: priceData.price_list,
                discount_formula: priceData.discount_formula,
              };
            } else {
              overrides[itemCode] = {
                unit_value: basePrice,
                price_list: '',
                discount_formula: comp?.DiscountFormula || '',
              };
              plNotes[itemCode] = ['Listino ' + priceList + ' non trovato, usato prezzo base'];
            }
          });

          this.kitComponentPriceOverrides = overrides;
          this.kitPriceListNotes = plNotes;
        }
      } catch (e) {
        this.showToast('Errore nel recupero prezzi listino kit', 'error');
      } finally {
        this.kitPriceListLoading = false;
      }
    },
    isKitChild(item) {
      if (!this.handleKits) return false;
      if (item._kitParentGroupId) return true;
      return !!(item.kit_parent_id && item.id && item.kit_parent_id != item.id);
    },
    isKitParent(item) {
      if (!this.handleKits) return false;
      if (item._kitGroupId) return true;
      return !!(item.kit_parent_id && item.id && item.kit_parent_id == item.id);
    },
    editKitDetail(item) {
      const kitGroupId = item._kitGroupId;
      const existingChildren = kitGroupId
        ? this.localDetails.filter(d => d._kitParentGroupId === kitGroupId)
        : this.localDetails.filter(d => d.kit_parent_id == item.id && d.id != item.id);

      this.isLoading = true;
      this.loadingText = 'Caricamento componenti kit...';
      axios.post(route('modules.ordini.getKitData'), { item: item.item })
        .then(response => {
          if (response.data.success) {
            const serverComponents = response.data.components;
            const serverNotes = response.data.notes || {};

            const existingByItem = {};
            existingChildren.forEach(c => { existingByItem[c.item] = c; });

            const mergedComponents = [...serverComponents];
            existingChildren.forEach(child => {
              if (!mergedComponents.find(c => c.Component === child.item)) {
                mergedComponents.push({
                  Component: child.item,
                  Description: child.description || child.item,
                  BaseUOM: child.actual_uom || 'NR',
                  IssuedQty: 1,
                  _kitIssuedQty: this.kitQty > 0 ? child.qty / this.kitQty : 1,
                  Line: 9999,
                  PkObbligatorio: 0,
                  BasePrice: child.unit_value || 0,
                  TaxCode: child.tax_code || '',
                  Discount1: child.discount1 || '',
                  Discount2: child.discount2 || '',
                  DiscountFormula: child.discount_formula || '',
                });
              }
            });

            this.kitComponents = mergedComponents;
            this.kitPendingDetail = item;
            this.kitQty = item.qty || 1;
            this.kitSelectedComponents = existingChildren.map(c => c.item);

            const qtys = {};
            existingChildren.forEach(c => { qtys[c.item] = c.qty; });
            serverComponents.forEach(c => {
              if (!qtys[c.Component]) {
                qtys[c.Component] = parseFloat(c._kitIssuedQty ?? c.IssuedQty) || 1;
              }
            });
            this.kitComponentQtys = qtys;

            const notes = { ...serverNotes };
            existingChildren.forEach(c => {
              if (!notes[c.item] && serverNotes[c.item]) notes[c.item] = serverNotes[c.item];
            });
            this.kitNotes = notes;

            this.kitPriceList = item.price_list || '';
            this.kitComponentPriceOverrides = {};
            this.kitPriceListNotes = {};
            this.kitComponentWeee = response.data.weee || {};
            this.kitNota = item.nota || '';
            this.kitExpectedDelivery = item.expected_delivery_date || null;

            this.showKitModal = true;
          } else {
            this.editDetail(item);
          }
        })
        .catch(() => { this.editDetail(item); })
        .finally(() => { this.isLoading = false; this.loadingText = ''; });
    },
    confirmKitSelection() {
      this.showKitModal = false;

      const kitGroupId = this.kitPendingDetail._kitGroupId || ('_kit_' + Date.now());

      const isExistingKit = this.localDetails.some(d =>
        (this.kitPendingDetail._kitGroupId && d._kitGroupId === this.kitPendingDetail._kitGroupId) ||
        (this.kitPendingDetail.id && d.id === this.kitPendingDetail.id)
      );

      const selectedComponents = this.kitComponents.filter(c =>
        this.kitSelectedComponents.includes(c.Component)
      );

      if (isExistingKit) {
        const parentIndex = this.localDetails.findIndex(d =>
          (this.kitPendingDetail._kitGroupId && d._kitGroupId === this.kitPendingDetail._kitGroupId) ||
          (this.kitPendingDetail.id && d.id === this.kitPendingDetail.id)
        );
        if (parentIndex !== -1) {
          this.localDetails[parentIndex] = {
            ...this.localDetails[parentIndex],
            qty: this.kitQty,
            _kitGroupId: kitGroupId,
            price_list: this.kitPriceList,
            nota: this.kitNota,
            expected_delivery_date: this.kitExpectedDelivery,
          };
        }
        this.localDetails = this.localDetails.filter(d => {
          if (this.kitPendingDetail._kitGroupId && d._kitParentGroupId === this.kitPendingDetail._kitGroupId) return false;
          if (this.kitPendingDetail.id && d.kit_parent_id && d.kit_parent_id == this.kitPendingDetail.id && d.id != this.kitPendingDetail.id) return false;
          return true;
        });

        const parentRow = this.localDetails.find(d =>
          (this.kitPendingDetail._kitGroupId && d._kitGroupId === kitGroupId) ||
          (this.kitPendingDetail.id && d.id === this.kitPendingDetail.id)
        );
        const insertAfterPosition = parentRow ? parentRow.position : this.localDetails.length;

        this.localDetails.forEach(d => {
          if (d.position > insertAfterPosition) {
            d.position += selectedComponents.length;
          }
        });

        selectedComponents.forEach((component, i) => {
          const rawQty = parseFloat(this.kitComponentQtys[component.Component]);
          const priceOverride = this.kitComponentPriceOverrides[component.Component];
          const unitValue = priceOverride ? priceOverride.unit_value : (parseFloat(component.BasePrice) || 0);
          const discountFormula = priceOverride?.discount_formula ?? component.DiscountFormula ?? '';
          const weee = this.handleWeee ? (this.kitComponentWeee[component.Component] || {}) : {};
          const qty = !isNaN(rawQty) ? rawQty : (parseFloat(component._kitIssuedQty ?? component.IssuedQty) || 1) * this.kitQty;
          const weeeUomAmount = weee.weee_uom_amount ?? 0;
          const componentRow = {
            id: null,
            item: component.Component,
            description: component.Description || component.Component,
            line_type: this.MagoEnums.Line_Type.Goods,
            sale_type: this.MagoEnums.Sale_Type.Normal,
            base_uom: component.BaseUOM || 'NR',
            actual_uom: component.BaseUOM || 'NR',
            previous_uom: component.BaseUOM || 'NR',
            qty,
            unit_value: unitValue,
            discount_formula: discountFormula,
            discount1: component.Discount1 || 0,
            discount2: component.Discount2 || 0,
            net_price: 0,
            tax_code: component.TaxCode || '',
            tax_percentage: 0,
            tax_nocharges: false,
            taxable_amount: 0,
            tax_amount: 0,
            total_amount: 0,
            position: insertAfterPosition + i + 1,
            price_list: this.kitPriceList,
            salesperson_comm: 0,
            nota: '',
            expected_delivery_date: this.kitExpectedDelivery,
            weee_amount: qty * weeeUomAmount,
            weee_tax_code: weee.weee_tax_code ?? '22',
            weee_tax_percentage: weee.weee_tax_percentage ?? 22,
            weee_uom_amount: weeeUomAmount,
            weee_ctg: weee.weee_ctg ?? '',
            custom: false,
            kit_parent_id: null,
            _kitParentGroupId: kitGroupId,
          };
          this.localDetails.push(componentRow);
        });
        this.fixPositions();
      } else {
        const kitDetail = {
          ...this.kitPendingDetail,
          qty: this.kitQty,
          line_type: this.MagoEnums.Line_Type.Descriptive,
          _kitGroupId: kitGroupId,
          price_list: this.kitPriceList,
          nota: this.kitNota,
          expected_delivery_date: this.kitExpectedDelivery,
        };
        this.localDetails.push(kitDetail);
        this.fixPositions();

        selectedComponents.forEach(component => {
          const rawQty = parseFloat(this.kitComponentQtys[component.Component]);
          const priceOverride = this.kitComponentPriceOverrides[component.Component];
          const unitValue = priceOverride ? priceOverride.unit_value : (parseFloat(component.BasePrice) || 0);
          const discountFormula = priceOverride?.discount_formula ?? component.DiscountFormula ?? '';
          const weee = this.handleWeee ? (this.kitComponentWeee[component.Component] || {}) : {};
          const qty = !isNaN(rawQty) ? rawQty : (parseFloat(component._kitIssuedQty ?? component.IssuedQty) || 1) * this.kitQty;
          const weeeUomAmount = weee.weee_uom_amount ?? 0;
          const componentRow = {
            id: null,
            item: component.Component,
            description: component.Description || component.Component,
            line_type: this.MagoEnums.Line_Type.Goods,
            sale_type: this.MagoEnums.Sale_Type.Normal,
            base_uom: component.BaseUOM || 'NR',
            actual_uom: component.BaseUOM || 'NR',
            previous_uom: component.BaseUOM || 'NR',
            qty,
            unit_value: unitValue,
            discount_formula: discountFormula,
            discount1: component.Discount1 || 0,
            discount2: component.Discount2 || 0,
            net_price: 0,
            tax_code: component.TaxCode || '',
            tax_percentage: 0,
            tax_nocharges: false,
            taxable_amount: 0,
            tax_amount: 0,
            total_amount: 0,
            position: this.localDetails.length + 1,
            price_list: this.kitPriceList,
            salesperson_comm: 0,
            nota: '',
            expected_delivery_date: this.kitExpectedDelivery,
            weee_amount: qty * weeeUomAmount,
            weee_tax_code: weee.weee_tax_code ?? '22',
            weee_tax_percentage: weee.weee_tax_percentage ?? 22,
            weee_uom_amount: weeeUomAmount,
            weee_ctg: weee.weee_ctg ?? '',
            custom: false,
            kit_parent_id: null,
            _kitParentGroupId: kitGroupId,
          };
          this.localDetails.push(componentRow);
          this.fixPositions();
        });
      }

      this.hasUnsavedChanges = true;
      this.kitPendingDetail = null;
      this.kitComponents = [];
      this.kitSelectedComponents = [];
      this.kitComponentQtys = {};
      this.kitNotes = {};
      this.kitChangingComponent = null;
      this.kitPriceList = '';
      this.kitComponentPriceOverrides = {};
      this.kitPriceListNotes = {};
      this.kitNota = '';
      this.kitExpectedDelivery = null;
      this.kitComponentWeee = {};
      this.calculateOrderTotalsFromServer();
    },
    openNotePanel(){
      const newDetail = {
        id: null, // Le nuove righe non hanno ID fino al salvataggio
        item: null,
        description: '',
        line_type: this.MagoEnums.Line_Type.Notes, // Nota
        sale_type: this.MagoEnums.Sale_Type.Normal, // Normale
        base_uom: '',
        actual_uom: '',
        previous_uom: '',
        qty: 0,
        unit_value: 0,
        discount_formula: '',
        discount1: 0,
        discount2: 0,
        net_price: 0,
        tax_code: '',
        tax_percentage: 0,
        tax_nocharges: false,
        taxable_amount: 0,
        tax_amount: 0,
        total_amount: 0,
        position: this.localDetails.length + 1, // Prossima posizione disponibile
        price_list: '',
        //additional_description: '',
        salesperson_comm: 0,
        nota: '',
        weee_amount: 0,
        weee_tax_code: '22',
        weee_tax_percentage: 22,
        weee_uom_amount: 0,
        weee_ctg: '',
        custom: false,
        exclude_from_invoice: this.noteRowsExcludeFromInvoiceDdt ? true : false,
        exclude_from_ddt: this.noteRowsExcludeFromInvoiceDdt ? true : false,
      };
      
      this.showArticleModal = false;

      // Open modal for new detail instead of adding directly
      this.editDetail(newDetail, true);
    },
    fuoriMagazzino() {
      
      let item = '';
      let description = '';
      let line_type = this.MagoEnums.Line_Type.Goods;

      // Stai arrivando dal modale della ricerca articolo
      if (this.showArticleModal)
      {
        item = this.articleTableHeaders.find(x => x.value === 'Item').filtervalue || '';
        description = this.articleTableHeaders.find(x => x.value === 'Description').filtervalue || '';
        line_type = this.articleServiceFilter == 1 ? this.MagoEnums.Line_Type.Service : this.MagoEnums.Line_Type.Goods;
        this.showArticleModal = false;
      }

      const newDetail = {
        id: null, // Le nuove righe non hanno ID fino al salvataggio
        item: item,
        description: description,
        line_type: line_type, // Servizio o Merce
        sale_type: this.MagoEnums.Sale_Type.Normal, // Normale
        base_uom: '',
        actual_uom: '',
        previous_uom: '',
        qty: 1,
        unit_value: 0,
        discount_formula: '',
        discount1: '',
        discount2: '',
        net_price: 0,
        tax_code: '22',
        tax_percentage: 22,
        tax_nocharges: false,
        taxable_amount: 0,
        tax_amount: 0,
        total_amount: 0,
        position: this.localDetails.length + 1, // Prossima posizione disponibile
        price_list: '',
        //additional_description: '',
        salesperson_comm: 0,
        nota: '',
        weee_amount: 0,
        weee_tax_code: '22',
        weee_tax_percentage: 22,
        weee_uom_amount: 0,
        weee_ctg: '',
        custom: true,
      };
      
      this.editDetail(newDetail, true);

    },
    showTablePayments() {
      this.deepTableTitle = this.$t('go.deep_tables.select_payment');
      this.deepTableDefine = true;
      this.deepTableType = 'Payments';
      this.handleDeepTableClick = this.paymentsCallback;
      this.loadDeepTable(undefined, undefined, undefined)
    },
    paymentsCallback(event, row) {
      this.localHead.payment_term = row.item;
      this.localHead.discount_formula = row.item.DiscountFormula;
      if (this.localHead.customer_data?.DebitCollectionCharges == '1' && row.item.CollectionCharges > 0)
      {
        this.localHead.collection_charges_price = row.item.CollectionCharges;
      }
      else
      {
        this.localHead.collection_charges_price = null;
      }

      this.closeDeepTable();
      // Ricalcola i totali dell'ordine dopo aver cambiato il payment term
      this.calculateOrderTotalsFromServer();
    },
    showTableCustomer() {
      this.contactSelection = false;
      this.deepTableTitle = this.$t('go.deep_tables.select_customer');
      this.deepTableDefine = true;
      this.deepTableType = 'Customers';
      this.handleDeepTableClick = this.customerCallback;
      this.loadDeepTable(undefined, undefined, undefined)
    },
    customerCallback(event, row) {
      this.localHead.customer_data = row.item;
      this.localHead.is_contact = 0;
      this.localHead.delivery_branch = '';
      this.localHead.linked_people = '';
      this.closeDeepTable();
      this.loadPayment();
      this.getBranches();

      if (row.item.SalesPerson && row.item.SalesPerson !== '' && row.item.SalesPerson !== this.localHead.sales_person) {
        this.showAgentConfirmModal = true;
      }
      this.hasUnsavedChanges = true;
    },
    showTableContact() {
      this.contactSelection = true;
      this.deepTableTitle = this.$t('go.deep_tables.select_contact');
      this.deepTableDefine = true;
      this.deepTableType = 'Contacts';
      this.handleDeepTableClick = this.contactCallback;
      this.loadDeepTable(undefined, undefined, undefined)
    },
    contactCallback(event, row) {
      this.localHead.customer_data = row.item;
      this.localHead.is_contact = 1;
      this.localHead.delivery_branch = '';
      this.localHead.linked_people = '';
      this.peopleOptions = [];
      this.closeDeepTable();
      this.loadPayment();
      this.getBranches();

      if (row.item.SalesPerson && row.item.SalesPerson !== '' && row.item.SalesPerson !== this.localHead.sales_person) {
        this.showAgentConfirmModal = true;
      }
      this.hasUnsavedChanges = true;
    },
    showTableHomogeneousCtg() {
      this.deepTableTitle = this.$t('go.deep_tables.select_homogeneous_ctg');
      this.deepTableDefine = true;
      this.deepTableType = 'HomogeneousCtg';
      this.handleDeepTableClick = this.homogeneousCtgCallback;
      this.loadDeepTable(undefined, undefined, undefined)
    },
    homogeneousCtgCallback(event, row) {
      this.articleTableHeaders.find(x => x.value === 'HomogeneousCtg').filtervalue = row.item.Category;
      this.loadArticleTable(undefined, undefined, undefined);
      this.closeDeepTable();
    },
    showTableCommodityCtg() {
      this.deepTableTitle = this.$t('go.deep_tables.select_commodity_ctg');
      this.deepTableDefine = true;
      this.deepTableType = 'CommodityCtg';
      this.handleDeepTableClick = this.commodityCtgCallback;
      this.loadDeepTable(undefined, undefined, undefined)
    },
    commodityCtgCallback(event, row) {
      this.articleTableHeaders.find(x => x.value === 'CommodityCtg').filtervalue = row.item.Category;
      this.loadArticleTable(undefined, undefined, undefined);
      this.closeDeepTable();
    },
    showTableProductCtg() {
      this.deepTableTitle = this.$t('go.deep_tables.select_product_ctg');
      this.deepTableDefine = true;
      this.deepTableType = 'ProductCtg';
      this.handleDeepTableClick = this.productCtgCallback;
      this.loadDeepTable(undefined, undefined, undefined)
    },
    productCtgCallback(event, row) {
      this.articleTableHeaders.find(x => x.value === 'ProductCtg').filtervalue = row.item.Category;
      this.loadArticleTable(undefined, undefined, undefined);
      this.closeDeepTable();
    },
    showTableProducer() {
      this.deepTableTitle = this.$t('go.deep_tables.select_producer');
      this.deepTableDefine = true;
      this.deepTableType = 'Producers';
      this.handleDeepTableClick = this.producerCallback;
      this.loadDeepTable(undefined, undefined, undefined)
    },
    producerCallback(event, row) {
      this.articleTableHeaders.find(x => x.value === 'Producer').filtervalue = row.item.Producer;
      this.loadArticleTable(undefined, undefined, undefined);
      this.closeDeepTable();
    },
    showTableTaxCode() {
      this.deepTableTitle = this.$t('go.deep_tables.select_tax_code');
      this.deepTableDefine = true;
      this.deepTableType = 'TaxCodes';
      this.handleDeepTableClick = this.taxCodeCallback;
      this.loadDeepTable(undefined, undefined, undefined)
    },
    taxCodeCallback(event, row) {
      this.detailData.tax_code = row.item.TaxCode;
      this.detailData.tax_percentage = parseFloat(row.item.Perc) || 0;
      this.detailData.tax_nocharges = row.item.NoChargesDistribution == '1';
      this.closeDeepTable();
      
      // Forza il ricalcolo dei totali immediatamente
      this.calculateRowTotalsFromServer();
    },
    showTableCollectionTaxCode() {
      this.deepTableTitle = this.$t('go.deep_tables.select_collection_tax_code');
      this.deepTableDefine = true;
      this.deepTableType = 'TaxCodes';
      this.handleDeepTableClick = this.collectionTaxCodeCallback;
      this.loadDeepTable(undefined, undefined, undefined)
    },
    collectionTaxCodeCallback(event, row) {
      this.localHead.collection_tax_code = row.item.TaxCode;
      this.localHead.collection_tax_percentage = parseFloat(row.item.Perc) || 0;
      this.closeDeepTable();
      
      // Ricalcola i totali dell'ordine dopo aver cambiato il codice IVA di incasso
      this.calculateOrderTotalsFromServer();
    },
    showTableItemType() {
      this.deepTableTitle = this.$t('go.deep_tables.select_item_type');
      this.deepTableDefine = true;
      this.deepTableType = 'ItemTypes';
      this.handleDeepTableClick = this.itemTypeCallback;
      this.loadDeepTable(undefined, undefined, undefined)
    },
    itemTypeCallback(event, row) {
      this.articleTableHeaders.find(x => x.value === 'ItemType').filtervalue = row.item.CodeType;
      this.loadArticleTable(undefined, undefined, undefined);
      this.closeDeepTable();
    },
    loadArticleTable(sortby = undefined, itemsperpage = undefined, page = undefined, afterdeletion = false) {
      if (sortby) this.articleTablesortby = sortby;
      if (itemsperpage) this.articleTableipp = itemsperpage;
      if (page) this.articleTablepage = page;

      const requestData = { 
        itemfilter: this.articleServiceFilter, 
        headers: this.articleTableHeaders.filter(x => x.value != 'actions'), 
        sortby: this.articleTablesortby, 
        itemsperpage: this.articleTableipp, 
        page: this.articleTablepage,
        show_disabled: this.showDisabled,
        reference: this.localHead.is_contact == 1 ? this.localHead.customer_data?.Contact : this.localHead.customer_data?.CustSupp
      };

      if (this.embedded) {
        // Usiamo axios in embedded mode per evitare la navigazione
        
        axios.post(route('modules.ordini.articletable'), requestData, {
          headers: {
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            'Content-Type': 'application/json',
            'X-Inertia': 'true',
            'X-Inertia-Version': usePage().version ?? ''
          }
        }).then(response => {
          this.localArticleTableItems = response.data.props.articleTableItems;
          this.localArticleTableCount = response.data.props.articleTableCount;
          this.localArticleTablesortby = response.data.props.articleTablesortby;
          this.showArticleModal = true;
        }).catch(error => {
          console.error(error)
        });
      } else {
        // Usiamo router.post in modalità normale
        router.post(route('modules.ordini.articletable'), requestData, {
          only: ['articleTableItems', 'articleTableCount', 'articleTablesortby'], preserveUrl: true, onSuccess: (response) => {
            this.localArticleTableItems = response.props.articleTableItems;
            this.localArticleTableCount = response.props.articleTableCount;
            this.localArticleTablesortby = response.props.articleTablesortby;
            this.showArticleModal = true;
          }
        });
      }
    },
    loadDeepTable(sortby = undefined, itemsperpage = undefined, page = undefined, afterdeletion = false) {
      /*if (afterdeletion)
      return;*/

      const requestData = { 
        type: this.deepTableType, 
        newload: this.deepTableDefine, 
        headers: this.localDeepTableHeaders, 
        sortby: sortby ?? this.localDeepTablesortby, 
        itemsperpage: itemsperpage ?? this.localDeepTableipp, 
        page: page ?? this.localDeepTablepage,
        show_disabled: this.showDisabled
      };

      if (this.embedded) {
        axios.post(route('modules.ordini.deeptable'), requestData, {
          headers: {
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            'Content-Type': 'application/json',
            'X-Inertia': 'true',
            'X-Inertia-Version': usePage().version ?? ''
          }
        }).then(response => {
          this.localDeepTableHeaders = response.data.props.deepTableHeaders;
          this.localDeepTablesortby = response.data.props.deepTablesortby;
          this.localDeepTableipp = response.data.props.deepTableipp;
          this.localDeepTablepage = response.data.props.deepTablepage;
          this.localDeepTablecount = response.data.props.deepTablecount;
          this.localDeepTableItems = response.data.props.deepTableItems;
          this.deepTableDefine = false;
          this.showDeepestSelectionTableModal = true;
        }).catch(error => {
          console.error(error)
        });
      } else {
        router.post(route('modules.ordini.deeptable'), requestData, {
          only: ['deepTableItems', 'deepTablecount', 'deepTableHeaders', 'deepTablesortby', 'deepTableipp', 'deepTablepage'], 
          preserveUrl: true, 
          onSuccess: (response) => {
            this.localDeepTableHeaders = response.props.deepTableHeaders;
            this.localDeepTablesortby = response.props.deepTablesortby;
            this.localDeepTableipp = response.props.deepTableipp;
            this.localDeepTablepage = response.props.deepTablepage;
            this.deepTableDefine = false;
            this.showDeepestSelectionTableModal = true;
          }
        });
      }
    },
    showTableOffert() {
      this.deepTableTitle = this.$t('go.deep_tables.select_offer');
      this.deepTableDefine = true;
      this.deepTableType = 'Offert';
      this.handleDeepTableClick = this.offertCallback;
      this.getOffertList(undefined, undefined, undefined)
    },
    offertCallback(event, row) {
      const requestData = { id: row.item.id, currentno: this.localHead.doc_no };
      this.isLoading = true;

      if (this.embedded) {
        axios.post(route('modules.ordini.importofferta'), requestData, {
          headers: {
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            'Content-Type': 'application/json',
            'X-Inertia': 'true',
            'X-Inertia-Version': usePage().version ?? ''
          }
        }).then(response => {
          this.isLoading = false;
          const data = response.data;
          if (data.success === false) {
            this.showToast(data.message || this.$t('go.messages.import_error'), 'error');
            return;
          }
          const props = data.props ?? data;
          if (props.head) {
            Object.assign(this.localHead, props.head);
          }
          if (props.details) {
            this.localDetails = [...props.details];
          }
          this.fixPositions();
          this.showToast(this.$t('go.messages.import_success'), "success");
          this.closeDeepTable();
          this.getBranches();
        }).catch(error => {
          console.error(error)
          this.isLoading = false;
          const message = error.response?.data?.message ?? this.$t('go.messages.import_error');
          this.showToast(message, 'error');
        });
      } else {
        this.isLoading = true;
        router.post(route('modules.ordini.importofferta'), requestData, {
          only: ['head', 'details'], preserveUrl: true, onSuccess: (response) => {
            this.isLoading = false;
            this.localDetails = [...response.props.details];
            
            this.fixPositions();
            
            this.showToast(this.$t('go.messages.import_success'), "success");
            this.closeDeepTable();
            this.getBranches();
          },
          onError: () => {
            this.isLoading = false;
            this.showToast(this.$t('go.messages.import_error'), 'error');
          }
        });
      }
    },
    getOffertList(sortby = undefined, itemsperpage = undefined, page = undefined, afterdeletion = false) {
      /*if (afterdeletion)
      return;*/

      //sortBy e ipp passano solo il valore aggiornato come $event, non si modifica realmente, quindi ricaricare le prop
      let data = { type: this.deepTableType, newload: this.deepTableDefine, headers: this.localDeepTableHeaders, sortby: sortby ?? this.localDeepTablesortby, itemsperpage: itemsperpage ?? this.localDeepTableipp, page: page ?? this.localDeepTablepage };
      
      if (this.embedded) {
        axios.post(route('modules.ordini.offertforimport'), data, {
          headers: {
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            'Content-Type': 'application/json',
            'X-Inertia': 'true',
            'X-Inertia-Version': usePage().version ?? ''
          }
        }).then(response => {
          this.localDeepTablecount = response.data.props.deepTablecount;
          this.localDeepTableItems = response.data.props.deepTableItems;
          this.localDeepTableHeaders = response.data.props.deepTableHeaders;
          this.localDeepTablesortby = response.data.props.deepTablesortby;
          this.localDeepTableipp = response.data.props.deepTableipp;
          this.localDeepTablepage = response.data.props.deepTablepage;
          this.deepTableDefine = false;
          this.showDeepestSelectionTableModal = true;
        }).catch(error => {
          console.error(error)
        });
      } else {
        router.post(route('modules.ordini.offertforimport'), data, {
          only: ['deepTableItems', 'deepTablecount', 'deepTableHeaders', 'deepTablesortby', 'deepTableipp', 'deepTablepage'], preserveUrl: true, onSuccess: (response) => {
            this.localDeepTableHeaders = response.props.deepTableHeaders;
            this.localDeepTablesortby = response.props.deepTablesortby;
            this.localDeepTableipp = response.props.deepTableipp;
            this.localDeepTablepage = response.props.deepTablepage;
            this.deepTableDefine = false;
            this.showDeepestSelectionTableModal = true;
          }
        });
      }
    },
    closeArticleModal() {
      this.showArticleModal = false;
      this.kitChangingComponent = null;
    },
    closeDeepTable() {
      this.showDeepestSelectionTableModal = false;
    },
    editDetail(item, isNew = false) {
      this.editDetailModal = true;
      this.isNewDetail = isNew;
      this.currentItemNotes = [];
      this.warnQty = 0;
      this.packageQty = 0;
      this.detailData = JSON.parse(JSON.stringify(item)); // Clona l'oggetto per evitare modifiche dirette
      this.detailData.previous_uom = this.detailData.actual_uom; // Salva l'UM di base per il confronto

      // Calcola i totali iniziali dal server
      if (this.detailData.line_type != this.MagoEnums.Line_Type.Notes) // Non calcolare per le note
        this.calculateRowTotalsFromServer();
    },
    async applyDetailChanges() {
      if (this.detailData.line_type != this.MagoEnums.Line_Type.Notes) { // Non validare per le note
        // Aspetta che i calcoli siano terminati se ce ne sono in corso
        if (this.isCalculatingRowTotals) {
          while (this.isCalculatingRowTotals) {
            await new Promise(resolve => setTimeout(resolve, 100));
          }
        }

        // Validazione dei dati
        if (!this.detailData.qty || this.detailData.qty <= 0) {
          this.showToast(this.$t('go.messages.qty_must_be_positive'), 'error');
          return;
        }

        if (!this.allowPriceRowZero && (!this.detailData.unit_value || this.detailData.unit_value < 0)) {
          this.showToast(this.$t('go.messages.unit_value_required'), 'error');
          return;
        }

        if (this.detailData.actual_uom == '') {
          this.showToast(this.$t('go.messages.uom_required'), 'error');
          return;
        }

        if (this.detailData.custom) {

          if (!this.detailData.item && !this.detailData.description) {
            this.showToast(this.$t('go.messages.item_or_desc_required'), 'error');
            return;
          }

          console.log('Validazione riga personalizzata in corso...', this.detailData);

          if (this.detailData.item) {

            const res = await axios.post(route('modules.ordini.checkItem'), { detail: this.detailData })
              .then(response => {
                console.log('Risposta validazione riga personalizzata:', response.data);
                if (response.data.exists === true) {
                  this.showToast(response.data.message || this.$t('go.messages.item_already_in_mago'), 'error');
                  return -1 ; 
                } 
                else {
                  return 0; 
                }
              })
              .catch(error => {
                console.error('Errore nella validazione della riga personalizzata:', error);
                this.showToast(this.$t('go.messages.validation_error'), 'error');
                return -1;
              });

            if (res == -1) {
              return;
            }
          }
        }

        // Mostra popup tutte le volte se necessario
        if (this.warnQty > 0) {
          this.showWarnQtyModal = true;
          return;
        }
      }

      this.confirmApplyDetailChanges();
    },
    confirmApplyDetailChanges() {
      if (this.isNewDetail) {
        // Adding new detail
        this.localDetails.push({ ...this.detailData });
        this.fixPositions();
        this.showToast(this.$t('go.messages.row_added'), 'success');
      } else {
        // Editing existing detail
        const index = this.localDetails.findIndex(detail => detail.position === this.detailData.position);
        if (index !== -1) {
          this.localDetails[index] = { ...this.detailData };
        }
        this.showToast(this.$t('go.messages.changes_applied'), 'success');
      }

      // Segna come modificato
      this.hasUnsavedChanges = true;

      this.editDetailModal = false;
      this.isNewDetail = false;
      this.showWarnQtyModal = false;

      // Ricalcola i totali dell'ordine 
      this.calculateOrderTotalsFromServer();
    },
    confirmAgentChange() {
      this.localHead.sales_person = this.localHead.customer_data.SalesPerson;
      this.hasUnsavedChanges = true;
      this.showAgentConfirmModal = false;
    },
    cancelDetailEdit() {
      // Pulisci il timeout se esiste
      if (this.calculateTimeout) {
        clearTimeout(this.calculateTimeout);
        this.calculateTimeout = null;
      }
      this.isCalculatingRowTotals = false;

      if (this.isNewDetail) {
        // If canceling a new detail, just show a message
        this.showToast(this.$t('go.messages.add_cancelled'), 'info');
      }

      this.editDetailModal = false;
      this.detailData = null;
      this.isNewDetail = false;
    },
    calculateRowTotalsFromServer(changedField = '') {

      this.isCalculatingRowTotals = true;

      // Debounce per evitare troppe chiamate
      clearTimeout(this.calculateTimeout);
      this.calculateTimeout = setTimeout(() => {

        // Crea una copia dei dati per evitare problemi con id null
        const dataToSend = {
          row:{
            ...this.detailData,
            // Se è una riga nuova (id null), usa un ID temporaneo per il calcolo
            id: this.detailData.id || 'temp_' + Date.now()
          },
          changed_field: changedField,
          mago_doc_order_id: this.localHead.mago_doc_order_id,
          customer: this.localHead.is_contact == 1 ? null : (this.localHead.customer_data?.CustSupp || null),
        };

        this.showLoader(this.$t('go.messages.updating_prices'));

        axios.post(route('modules.ordini.calculateTotals'), dataToSend)
          .then(response => {
            if (response.data.success) {
              
              // Aggiorna dati aggiuntivi di ritorno
              this.available_uoms = response.data.available_uoms;
              if (this.detailData.actual_uom == '') {
                this.detailData.actual_uom = this.available_uoms[0].id; 
              }
              this.currentItemNotes = response.data.notes || [];
              this.warnQty = response.data.warn_qty || 0;

              if (this.detailData.actual_uom == '') {
                this.detailData.actual_uom = this.available_uoms[0].id; 
              }
              
              // Aggiorna i valori calcolati dal server
              this.detailData.net_price = response.data.totals.net_price;
              this.detailData.taxable_amount = response.data.totals.taxable_amount;
              this.detailData.tax_amount = response.data.totals.tax_amount;
              this.detailData.total_amount = response.data.totals.total_amount;
              this.detailData.discount1 = response.data.totals.discount1;
              this.detailData.discount2 = response.data.totals.discount2;
              this.detailData.discount_formula = response.data.totals.discount_formula;
              this.detailData.unit_value = response.data.totals.unit_value;
              this.detailData.qty = response.data.totals.qty;
              this.detailData.previous_uom = this.detailData.actual_uom; // Aggiorna l'UM precedente dopo il calcolo
              this.detailData.weee_amount = response.data.weee_amount;
              if (this.packageMode && response.data.package_uom) {
                this.detailData.package_uom = response.data.package_uom;
                const factor = this.getPackageConversionFactor();
                if (factor > 0) {
                  this.packageQty = Math.round(this.detailData.qty / factor);
                }
              }
            }
            else {
              console.error('Calcolo dei totali fallito:', response.data.message);
              this.showToast(response.data.message, 'error');
            }
          })
          .catch(error => {    
            console.error('Errore nel calcolare i totali:', error);
            this.showToast(error, 'error');
            // Se il calcolo fallisce, almeno calcola i totali base localmente
            if (this.detailData.qty && this.detailData.unit_value) {
              this.detailData.taxable_amount = this.detailData.qty * this.detailData.unit_value;
              this.detailData.total_amount = this.detailData.taxable_amount;
            }
          })
          .finally(() => {
            this.hideLoader();
            this.isCalculatingRowTotals = false;
          });

      }, 100);
    },
    calculateOrderTotalsFromServer() {
      // Debounce per evitare troppe chiamate
      clearTimeout(this.calculateOrderTimeout);
      this.calculateOrderTimeout = setTimeout(() => {

        // Passa sempre tutti i dati per usare i modelli virtuali
        const data = {
          head_id: this.localHead.id,
          mago_doc_order_id: this.localHead.mago_doc_order_id,
          head: {
            discount_formula: this.localHead.discount_formula,
            allowances: this.localHead.allowances,
            transport_price: this.localHead.transport_price,
            other_price: this.localHead.other_price,
            collection_charges_price: this.localHead.collection_charges_price,
            collection_tax_code: this.localHead.collection_tax_code,
            collection_tax_percentage: this.localHead.collection_tax_percentage,
            carrier1: this.localHead.carrier1
          },
          details: this.localDetails
        };

        axios.post(route('modules.ordini.calculateOrderTotals'), data)
          .then(response => {
            if (response.data.success) {
              // Aggiorna i totali dell'ordine
              this.orderTotals.goods_total = response.data.goods_total;
              this.orderTotals.services_total = response.data.services_total;
              this.orderTotals.subtotal = response.data.calculated_subtotal;
              this.orderTotals.discount_amount = response.data.calculated_discount_amount;
              this.orderTotals.taxable_amount = response.data.calculated_taxable_amount;
              this.orderTotals.tax_amount = response.data.calculated_tax_amount;
              this.orderTotals.total_amount = response.data.calculated_total_amount;
              this.orderTotals.total_freesamples_amount = response.data.calculated_total_freesamples_amount;
              this.orderTotals.total_weee_amount = response.data.calculated_total_weee_amount;
              this.orderTotals.vat_breakdown = response.data.vat_breakdown;

              // Aggiorna anche i valori nel head per la sincronizzazione
              this.localHead.goods_total = response.data.goods_total;
              this.localHead.services_total = response.data.services_total;

              this.availabilities = response.data.availabilities;
              this.itemLocations = response.data.item_locations;
            }
          })
          .catch(error => {
            console.error('Errore nel calcolare i totali ordine:', error);
          });

      }, 300);
    },
    showLoader(text = '') {
      this.isLoading = true;
      this.loadingText = text;
    },
    hideLoader() {
      this.isLoading = false;
    },
    deleteDetail(item) {
      if (this.isKitParent(item)) {
        this.localDetails = this.localDetails.filter(d => {
          if (item._kitGroupId && d._kitParentGroupId === item._kitGroupId) return false;
          if (item.id && d.kit_parent_id && d.kit_parent_id == item.id && d.id != item.id) return false;
          return true;
        });
      }
      this.localDetails.splice(this.localDetails.indexOf(item), 1);

      // Sistemare le posizioni dopo l'eliminazione
      this.fixPositions();

      // Segna come modificato
      this.hasUnsavedChanges = true;

      this.showToast(this.$t('go.messages.row_removed'), 'success');

      // Ricalcola i totali dell'ordine
      this.calculateOrderTotalsFromServer();

      this.showDeleteModalRow = false;
    },
    async duplicateDocument() {
      this.showLoader('Creazione copia documento...');

      try {
        const response = await axios.get(route('modules.ordini.documento', {
          id: -1,
          type: this.localHead.doc_status === 'offert' ? 'draft' : this.localHead.doc_status,
        }), {
          headers: {
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            'X-Inertia': 'true',
            'X-Inertia-Version': usePage().version ?? ''
          }
        });
        const props = response.data?.props ?? response.data;
        const newDocumentHead = props.head;

        if (!newDocumentHead?.doc_no) {
          throw new Error('Numeratore documento non disponibile');
        }

        const copiedHead = JSON.parse(JSON.stringify(this.localHead));
        Object.assign(copiedHead, {
          id: -1,
          doc_no: newDocumentHead.doc_no,
          owner: newDocumentHead.owner,
          created_at: newDocumentHead.created_at,
          user_id: null,
          updated_at: null,
          mago_doc_no: null,
          mago_doc_quota_no: null,
          mago_doc_order_no: null,
          mago_doc_quota_id: null,
          mago_doc_order_id: null,
          new_order_number: null,
          revision_no: null,
          mailId: null,
          blocked: false,
          disabled: 0,
          approval: 0,
          correctable: false,
        });

        const sourceDetails = JSON.parse(JSON.stringify(this.localDetails || []));
        const kitGroupById = new Map();
        sourceDetails.forEach((detail, index) => {
          if (detail._kitGroupId) {
            kitGroupById.set(detail.id, detail._kitGroupId);
          } else if (detail.id && detail.kit_parent_id == detail.id) {
            kitGroupById.set(detail.id, `_duplicate_kit_${index}_${detail.id}`);
          }
        });
        const copiedDetails = sourceDetails.map(detail => ({
          ...detail,
          id: -1,
          weborders_head_id: -1,
          kit_parent_id: null,
          created_at: null,
          updated_at: null,
          contract_docno: null,
          _kitGroupId: detail._kitGroupId || kitGroupById.get(detail.id),
          _kitParentGroupId: detail._kitParentGroupId || kitGroupById.get(detail.kit_parent_id),
        }));

        this.stopHeartbeat();
        this.rilasciaLock();
        this.localHead = copiedHead;
        this.localDetails = copiedDetails;
        this.hasUnsavedChanges = true;
        this.showOrderDetails = true;
        this.getBranches();
        this.calculateOrderTotalsFromServer();
        this.showToast('Documento duplicato. Salva per creare la copia.', 'success');
      } catch (error) {
        console.error(error);
        this.showToast(error.response?.data?.message || 'Errore durante la duplicazione del documento.', 'error');
      } finally {
        this.hideLoader();
      }
    },
    deleteDocument(){
      this.showLoader(this.$t('go.messages.deleting'));

      axios.post(route('modules.ordini.deletedocument'), { id: this.localHead.id })
        .then(response => {
          this.hideLoader();
          if (response.data.success) {
            this.showToast(this.$t('go.messages.doc_deleted'), 'success');
            this.showDeleteModal = false;
            setTimeout(() => {
              window.location.href = route('modules.ordini.lista-ordini');
            }, 1500);
          }
          else
          {
            console.error(response.data)
            this.showToast(response.data.message || this.$t('go.messages.delete_error'), 'error');
          }
        })
        .catch(error => {
          console.error(error)
          this.hideLoader();
          
          let errorMessage = this.$t('go.messages.delete_error');
          this.showToast(errorMessage, 'error');
        });
    },
    getBranches() {
      if (this.localHead.customer_data?.CustSupp || this.localHead.customer_data?.Contact) {
        axios.post(route('modules.ordini.getbranches'), { reference: this.localHead.is_contact == 1 ? this.localHead.customer_data?.Contact : this.localHead.customer_data?.CustSupp, isContact: this.localHead.is_contact })
          .then(response => {
            if (response.data.success) {
              this.branches = response.data.branches;
              this.peopleOptions = response.data.people || [];
            }
          })
          .catch(error => {
            console.error('Errore nel recupero delle sedi:', error);
          });
      }
    },
    updateLinkedPeople(values) {
      this.localHead.linked_people = (values || []).join(',');
    },
    loadPayment() {
      axios.post(route('modules.ordini.referencepayment'), { reference: this.localHead.is_contact == 1 ? this.localHead.customer_data?.Contact : this.localHead.customer_data?.CustSupp, isContact: this.localHead.is_contact })
        .then(response => {
          if (response.data.success) {
            this.localHead.payment_term = response.data.data;
            this.localHead.discount_formula = response.data.DiscountFormula;

            if (this.localHead.customer_data?.DebitCollectionCharges == '1' && response.data.CollectionCharges > 0)
            {
              this.localHead.collection_charges_price = response.data.CollectionCharges;
            }
            else
            {
              this.localHead.collection_charges_price = null;
            }

            // Ricalcola i totali dell'ordine dopo aver caricato il payment term
            this.calculateOrderTotalsFromServer();
          }
        })
    },
    requiredFieldChecks() {
      return [
        {
          policyKey: 'dataConsegnaPrevista',
          value: this.localHead?.expected_delivery_date,
          label: this.$t('go.fields.expected_delivery'),
        },
       ];
    },
    validateRequiredFields() {
      return this.requiredFieldChecks()
        .filter(f => this.$policy(f.policyKey).isRequired && !f.value)
        .map(f => f.label);
    },
    saveChanges() {
      const missingFields = this.validateRequiredFields();
      if (missingFields.length > 0) {
        this.showToast('Campo obbligatorio mancante: ' + missingFields.join(', '), 'error');
        return Promise.reject();
      }
      this.showLoader(this.$t('go.messages.saving'));

      return new Promise((resolve, reject) => {
      
        const requestData = { 
          head: this.localHead, 
          details: this.localDetails 
        };

        const wasNew = this.localHead.id === -1;

        if (this.embedded) {
          axios.post(route('modules.ordini.save'), requestData, {
            headers: {
              'Accept': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
              'Content-Type': 'application/json',
              'X-No-Trim': 'true',
              'X-Inertia': 'true',
              'X-Inertia-Version': usePage().version ?? ''
            }
          }).then(response => {
            const props = response.data.props;
            if (props.head) {
              Object.assign(this.localHead, props.head);
            }
            if (props.details) {
              this.localDetails = [...props.details];
            }
            this.hasUnsavedChanges = false;
            this.hideLoader();
            this.showToast(this.$t('go.messages.doc_saved'), 'success');
            if (wasNew && this.localHead.id !== -1) {
              this.startHeartbeat();
            }
            resolve();
          }).catch(error => {
            console.error(error)
            this.hideLoader();
            const message = error.response?.data?.message ?? this.$t('go.messages.save_error');
            this.showToast(message, 'error');
            reject();
          });
        } else {
          router.post(route('modules.ordini.save'), requestData, {
            headers: {
              'X-No-Trim': 'true'
            },
            preserveScroll: true,
            onSuccess: (page) => {
              this.hasUnsavedChanges = false;
              this.hideLoader();
              this.showToast(this.$t('go.messages.doc_saved'), 'success');
              if (wasNew && page.props?.head?.id && page.props.head.id !== -1) {
                Object.assign(this.localHead, page.props.head);
                this.startHeartbeat();
              }
              resolve();
            },
            onError: (error) => {
              this.hideLoader();
              this.showToast(this.$t('go.messages.save_error'), 'error');
              reject();
            }
          });
        }
      });
    },
    async sendToMago() {
      await this.saveChanges();

      this.showLoader(this.$t('go.messages.sending_to_mago'));

      return new Promise((resolve, reject) => {
        if (this.embedded) {
          axios.post(route('modules.ordini.sendToMago', { id: this.localHead.id }), {}, {
            headers: {
              'Accept': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
              'Content-Type': 'application/json',
              'X-Inertia': 'true',
              'X-Inertia-Version': usePage().version ?? ''
            }
          }).then(response => {
            const props = response.data.props;
            if (props.head) {
              Object.assign(this.localHead, props.head);
            }
            if (props.error) {
              this.hideLoader();
              this.showToast(props.error, 'error');
            } else {
              this.hideLoader();
              this.showToast(this.$t('go.messages.order_sent_to_mago'), 'success');
              this.$emit('reload-mails');
              resolve();
            }
          }).catch(error => {
            this.hideLoader();
            const message = error.response?.data?.message ?? this.$t('go.messages.mago_send_error');
            this.showToast(message, 'error');
            reject();
          });
        } else {
          router.post(
            route('modules.ordini.sendToMago', { id: this.localHead.id }),
            {},
            {
              only: ['head', 'error'],
              preserveScroll: true,
              preserveState: true,
              preserveUrl: true,
              onSuccess: (response) => {
                this.hideLoader();
                if (this.error) {
                  this.showToast(this.error, 'error');
                }
                else this.showToast(this.$t('go.messages.order_sent_to_mago'), 'success');
                resolve();
              },
              onError: (error) => {
                this.hideLoader();
                this.showToast(this.$t('go.messages.mago_send_error'), 'error');
                reject();
              }
            }
          );
        }
      });
    },
    openCreateContractModal() {
      this.selectedContractRowIds = [];
      this.selectAllContractRows = false;
      this.showCreateContractModal = true;
    },
    onContractCreatedStorage(event) {
      if (event.key !== 'p93_contract_created' || !event.newValue) {
        return;
      }
      try {
        const data = JSON.parse(event.newValue);
        if (data.sourceOrderId && this.localHead?.id && String(data.sourceOrderId) === String(this.localHead.id)) {
          window.location.reload();
        }
      } catch (e) {
        // ignore malformed payload
      }
    },
    toggleSelectAllContractRows() {
      this.selectedContractRowIds = this.selectAllContractRows
        ? this.contractSelectableDetails.map(d => d.id)
        : [];
    },
    createContractFromOrder() {
      const selectedDetails = this.contractEligibleDetails.filter(d => this.selectedContractRowIds.includes(d.id));

      if (selectedDetails.length === 0) {
        return;
      }

      const customerData = this.localHead.customer_data || {};
      const orderDate = this.localHead.doc_date ? new Date(this.localHead.doc_date).toISOString() : null;

      const prefillData = {
        Customer: {
          id: customerData.CustSupp,
          name: customerData.CompanyName
        },
        SalePerson: this.localHead.sales_person,
        PaymentCondition: this.localHead.payment_term?.Payment,
        StipulationDate: orderDate,
        sourceOrderId: this.localHead.id,
        sourceDetailIds: selectedDetails.map(d => d.id),
        lines: selectedDetails.map(d => ({
          Item: d.item,
          Description: d.description,
          UoM: d.actual_uom || d.base_uom,
          Qty: d.qty,
          UnitValue: d.unit_value,
          ActivationDate: orderDate,
          _sourceDetailId: d.id
        }))
      };

      const form = document.createElement('form');
      form.method = 'POST';
      form.action = route('modules.contratti.nuovo');
      form.target = '_blank';

      const tokenInput = document.createElement('input');
      tokenInput.type = 'hidden';
      tokenInput.name = '_token';
      tokenInput.value = document.querySelector('meta[name="csrf-token"]')?.content ?? '';
      form.appendChild(tokenInput);

      const dataInput = document.createElement('input');
      dataInput.type = 'hidden';
      dataInput.name = 'prefillData';
      dataInput.value = JSON.stringify(prefillData);
      form.appendChild(dataInput);

      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);

      this.showCreateContractModal = false;
    },
    async sendQuoteToMago() {
      await this.saveChanges();

      this.showLoader(this.$t('go.messages.sending_quote_to_mago'));

      return new Promise((resolve, reject) => {
        if (this.embedded) {
          axios.post(route('modules.ordini.sendQuoteToMago', { id: this.localHead.id }), {}, {
            headers: {
              'Accept': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
              'Content-Type': 'application/json',
              'X-Inertia': 'true',
              'X-Inertia-Version': usePage().version ?? ''
            }
          }).then(response => {
            const props = response.data.props;
            if (props.head) {
              Object.assign(this.localHead, props.head);
            }
            if (props.error) {
              this.hideLoader();
              this.showToast(props.error, 'error');
            } else {
              this.hideLoader();
              this.showToast(this.$t('go.messages.offer_sent_to_mago'), 'success');
              resolve();
            }
          }).catch(error => {
            this.hideLoader();
            const message = error.response?.data?.message ?? this.$t('go.messages.mago_send_error');
            this.showToast(message, 'error');
            reject();
          });
        } else {
          router.post(
            route('modules.ordini.sendQuoteToMago', { id: this.localHead.id }),
            {},
            {
              only: ['head', 'error'],
              preserveScroll: true,
              preserveState: true,
              preserveUrl: true,
              onSuccess: (response) => {
                this.hideLoader();
                if (this.error) {
                  this.showToast(this.error, 'error');
                } else {
                  this.showToast(this.$t('go.messages.offer_sent_to_mago'), 'success');
                }
                resolve();
              },
              onError: (error) => {
                this.hideLoader();
                this.showToast(this.$t('go.messages.mago_send_error'), 'error');
                reject();
              }
            }
          );
        }
      });
    },
    printDocument() {
      // Apre la stampa in una nuova finestra
      const printUrl = route('modules.ordini.stampa', { id: this.localHead.id });
      window.open(printUrl, '_blank');
    },

    fixPositions() {
      if (!this.localDetails || this.localDetails.length === 0) return;
      
      const sortedDetails = [...this.localDetails].sort((a, b) => {
       return a.position - b.position;
      });
      
      sortedDetails.forEach((detail, index) => {
        detail.position = index + 1;
      });

      this.localDetails = sortedDetails;
    },
    getArticleInfo(item){
      this.openPricelistDirect = false;
      this.currentItem = item;
      this.showItemInfo = true;
    },
    openPricelistInfo(item){
      this.currentItem = item;
      this.openPricelistDirect = true;
      this.showItemInfo = true;
    },
    closeItemInfo(){
      this.showItemInfo = false;
      this.openPricelistDirect = false;
    },
    onPricelistSelected(pricelistName) {
      if (this.blockedEdits) return;
      this.closeItemInfo();
      this.detailData.price_list = pricelistName;
      this.calculateRowTotalsFromServer('price_list');
    },
    goBackToHome() {
      // Emit event to parent component (OrderPanel) to go back to home
      this.$emit('go-back-to-home');
    },
    caricaNoteCliente() {
      axios.post(route('modules.ordini.notecliente'), { customer: this.localHead.customer_data.CustSupp })
          .then(response => {
            if (response.data.success) {
              this.custsuppnote = response.data.custsuppnote;
              this.custsuppmdanote = response.data.custsuppmdanote;
              this.custsupprownotes = response.data.custsupprownotes;

              this.showCustomerNotes = true;
            }
          })
          .catch(error => {
            console.error("Errore nel recupero dei dati dell'articolo:", error);
          });
    },
    rinnovaBlocco() {
      if (!this.enableDocumentLocking || !this.localHead?.id || this.localHead.id === -1) return;
      axios.post(route('modules.ordini.heartbeat', { id: this.localHead.id }))
        .then(response => {
          if (response.data.kicked) {
            this.stopHeartbeat();
            this.showKickedModal = true;
          }
        })
        .catch(() => {});
    },
    stopHeartbeat() {
      if (this.heartbeatInterval) {
        clearInterval(this.heartbeatInterval);
        this.heartbeatInterval = null;
      }
    },
    startHeartbeat() {
      if (!this.enableDocumentLocking || !this.localHead?.id || this.localHead.id === -1) return;
      this.stopHeartbeat();
      this.heartbeatInterval = setInterval(this.rinnovaBlocco, 30000);
    },
    rilasciaLock() {
      if (!this.enableDocumentLocking || !this.localHead?.id || this.localHead.id === -1) return;
      axios.post(route('modules.ordini.releaseLock', { id: this.localHead.id })).catch(() => {});
    },
    forzaAccesso() {
      axios.post(route('modules.ordini.forceLock', { id: this.localHead.id }))
        .then(response => {
          if (response.data.success) {
            this.showLockModal = false;
            this.startHeartbeat();
          }
        })
        .catch(() => {});
    },
    onKickedConfirm() {
      this.showKickedModal = false;
      this.$emit('go-back-to-home');
    },
    clearSignature() {
      if (this.$refs.signaturePad) {
        this.$refs.signaturePad.clearSignature();
      }
    },
    openSignatureConfirm() {
      if (this.$refs.signaturePad) {
        const { isEmpty, data } = this.$refs.signaturePad.saveSignature();
        if (!isEmpty) {
          this.showSignatureConfirm = true;
        } else {
          this.showToast(this.$t('go.messages.signature_required'), 'warning');
        }
      }
    },
    async saveSignature(){
      this.showSignatureConfirm = false;
      if (this.$refs.signaturePad) {
        const { isEmpty, data } = this.$refs.signaturePad.saveSignature();
        if (!isEmpty) {
          await this.sendToMago();

          this.showLoader(this.$t('go.messages.saving_signature'));
          axios.post(route('modules.ordini.savesignature'), { id: this.localHead.id, signatureData: data })
            .then(response => {
              this.hideLoader();
              if (response.data.success) {
                this.showToast(this.$t('go.messages.signature_saved'), 'success');
                this.showSignatureModal = false;
                this.localHead.blocked = true;
              } else {
                this.showToast(response.data.message || this.$t('go.messages.signature_error'), 'error');
              }
            })
            .catch(error => {
              console.error(error)
              this.hideLoader();
              const message = error.response?.data?.message ?? this.$t('go.messages.signature_error');
              this.showToast(message, 'error');
            });
        } else {
          this.showToast(this.$t('go.messages.signature_required'), 'warning');
        }
      }
    },
    loadDMSRows(){
      axios.post(route('modules.ordini.allegatiDMS'), { saleordid: this.localHead.mago_doc_order_id })
        .then(response => {
          if (response.data.success) {
            this.dmsAttachments = response.data.attachments;
            this.showDMSAttachmentsModal = true;
          }
        })
        .catch(error => {
          console.error("Errore nel recupero delle righe DMS:", error);
        });
    },
    showDeepTableDisabled(){
      return (['HomogeneousCtg', 'ProductCtg', 'ItemTypes', 'CommodityCtg', 'Offert'].indexOf(this.deepTableType) == -1);
    },
    /*checkForCustomerEmailParameter() {
      const urlParams = new URLSearchParams(window.location.search);
      const customerEmail = urlParams.get('customerEmail');
      
      if (customerEmail && this.localHead && this.localHead.id === -1) {
        this.searchCustomerByEmail(customerEmail);
      }
    },*/
    
    /*async searchCustomerByEmail(email) {
      try {
        const response = await axios.post(route('modules.ordini.searchCustomerByEmail'), {
          email: email
        });

        if (response.data.success) {
          this.localHead.customer_data = response.data.customer;
          this.localHead.delivery_branch = '';
          
          this.loadPayment();
          this.getBranches();
          
          if (this.$toast) {
            this.$toast.success('Cliente trovato e precompilato automaticamente da email');
          }
        } else {
          if (this.$toast) {
            this.$toast.warning('Cliente non trovato con email: ' + email);
          }
        }
      } catch (error) {
        console.error(error)
        if (this.$toast) {
          this.$toast.error('Errore durante la ricerca del cliente');
        }
      }
    }*/
    resizeTextArea(element){
      element.style.height = 'auto'
      element.style.height = element.scrollHeight + 'px'
    },
    requestApproval() {
      this.localHead.approval = 1;
      this.saveChanges();
    },
    approveDocument() {
      this.localHead.approval = 0;
      this.saveChanges();
    },
    requestCorrection(){
      this.localHead.correctable = true;
      this.saveChanges();
    },
    /*rejectDocument() {
      this.localHead.approval = 3;
      this.saveChanges();
    },*/
    getDisplayPosition(item){
      if (item.line_type == this.MagoEnums.Line_Type.Notes) return 0;
      else return item.position;
    }, 
    getPackageConversionFactor() {
      if (!this.detailData?.package_uom) return 0;
      const uom = this.available_uoms.find(u => u.ComparableUoM == this.detailData.package_uom);
      if (!uom || !uom.CompUoMQty) return 0;
      return uom.BaseUoMQty / uom.CompUoMQty;
    },
    onPackageQtyChange() {
      const intQty = Math.trunc(this.packageQty);
      this.packageQty = intQty;
      const factor = this.getPackageConversionFactor();
      if (factor > 0) {
        this.detailData.qty = intQty * factor;
      }
      this.calculateRowTotalsFromServer('qty');
    },
    uomText(override = undefined) {
      const current_uom = this.available_uoms.find(u => u.ComparableUoM == (override || this.detailData.actual_uom));
      const base_uom = this.available_uoms.find(u => u.ComparableUoM == this.detailData.base_uom);
      if (current_uom && base_uom) {
          return "(" + parseFloat(current_uom.CompUoMQty) + " " + current_uom.Description + " → " + parseFloat(current_uom.BaseUoMQty) + " " + base_uom.Description + ")";
      }
    },
    interpolateWidthFromLength(length, points) {
      if (length <= points[0].len) return points[0].width;
      const last = points[points.length - 1];
      if (length >= last.len) return last.width;
      for (let i = 0; i < points.length - 1; i++) {
        const a = points[i];
        const b = points[i + 1];
        if (length >= a.len && length <= b.len) {
          const ratio = (length - a.len) / (b.len - a.len);
          return a.width + ratio * (b.width - a.width);
        }
      }
      return last.width;
    },
    rowTextWidth(column)
    {
      const points = [
        { len: 0, width: 0 },
        { len: 50, width: 150 },
        { len: 100, width: 200 },
        { len: 200, width: 300 },
        { len: 300, width: 350 },
        { len: 500, width: 400 },
        { len: 700, width: 450 },
        { len: 1000, width: 500 },
      ];
      let width = 0;
      this.localDetails.forEach(detail => {
        if (detail[column]) {
          width = Math.max(width, this.interpolateWidthFromLength(detail[column].length, points));
        }
      });
      return width + 'px';
    }
  },
  async mounted() {
    this.loadingText = this.$t('go.messages.loading');

    this.localDetails = this.details ? [...this.details] : [];
    
    this.localDeepTableItems = this.deepTableItems ? [...this.deepTableItems] : [];
    this.localDeepTablecount = this.deepTablecount || 0;
    
    this.localArticleTableItems = this.articleTableItems ? [...this.articleTableItems] : [];
    this.localArticleTableCount = this.articleTableCount || 0;
    this.localArticleTablesortby = this.articleTablesortby ? [...this.articleTablesortby] : [];


    if (!this.personalizzazioniImel) {
      this.detailHeaders.push({ title: this.$t('go.fields.position'), value: 'position', align: 'end', sortable: true });
    }
    this.detailHeaders.push({ title: this.$t('go.fields.row_type'), value: 'line_type' });
    this.detailHeaders.push({ title: this.$t('go.fields.row_status'), value: 'status' });
    this.detailHeaders.push({ title: this.$t('go.fields.item_code'), value: 'item' });
    this.detailHeaders.push({ title: this.$t('go.fields.description'), value: 'description',  });

    if (this.showRowPricelist) {
      this.detailHeaders.push({ title: this.$t('go.fields.pricelist'), value: 'price_list' });
    }

    this.detailHeaders.push({ title: this.$t('go.fields.uom_short'), value: 'actual_uom' });
    this.detailHeaders.push({ title: this.$t('go.fields.qty'), value: 'qty', align: 'end' });
    this.detailHeaders.push({ title: this.$t('go.fields.price'), value: 'unit_value', align: 'end'});
    this.detailHeaders.push({ title: this.$t('go.fields.discount'), value: 'discount_formula', align: 'end' });
    this.detailHeaders.push({ title: this.$t('go.fields.taxable'), value: 'taxable_amount', align: 'end' });
    this.detailHeaders.push({ title: this.$t('go.fields.row_note'), value: 'nota', align: 'start'});

    //if (this.showItemsExtraData) {
      //this.detailHeaders.push({ title: '% prov. agente', value: 'agent_commission_percentage', align: 'end' });
    //}

    /*if (this.handleWeee) {
      this.detailHeaders.push({ title: 'Categoria RAEE', value: 'weee_ctg', align: 'end' });
    }*/

    if (!this.personalizzazioniImel) {
      this.detailHeaders.push({ title: this.$t('go.fields.tax_code_col'), value: 'tax_code' });
      this.detailHeaders.push({ title: this.$t('go.fields.row_total'), value: 'total_amount', align: 'end' });
    }

    this.detailHeaders.push({ title: '', value: 'actions' });

    if (this.head) {
      this.localHead = JSON.parse(JSON.stringify(this.head));
      
      if (!this.localHead.doc_date) {
        this.localHead.doc_date = this.todayDate;
      }

      if (this.explicitReview && new URLSearchParams(window.location.search).get('explicitReview') === '1') {
        const match = String(this.localHead.doc_no || '').match(/^(.*?)(?:_(\d+))?$/);
        this.localHead.doc_no = `${match[1]}_${Number(match[2] || 0) + 1}`;
        this.hasUnsavedChanges = true;
      }
    }

    // Set user's agent if available and not already set
    if (this.$page.props.user.agent && !this.localHead.sales_person) {
      this.localHead.sales_person = this.$page.props.user.agent;
    }

    this.getBranches();
    this.calculateOrderTotalsFromServer();

    if (this.enableDocumentLocking && this.localHead?.id && this.localHead.id !== -1) {
      if (this.documentLock) {
        this.showLockModal = true;
      } else {
        this.startHeartbeat();
      }
    }

    window.addEventListener('storage', this.onContractCreatedStorage);
  },
  unmounted() {
    this.stopHeartbeat();
    this.rilasciaLock();
    window.removeEventListener('storage', this.onContractCreatedStorage);
  }
};
</script>

<style scoped>
.clickinput {
  @apply fullinput pr-10 cursor-pointer;
}

.margininput {
  @apply fullinput pr-10 text-base;
}

.fullinput, .totalinput {
  @apply pl-3 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500
}

/* Il div parent di clickinput non ha un disabled nativo, vue compila valore o falso invece di aggiungere solo l'attributo, questo è un workaround */
.fullinput[disabled], div[disabled]:not([disabled="false"]):has(.clickinput) {
  @apply bg-gray-100 text-gray-500 pointer-events-none;
}

.fullinput option:disabled {
  display: none;
}
</style>